import React, { useCallback, useRef, useState } from "react";
import {
  Bookmark,
  Upload,
  Loader,
  Play,
  Plus,
  Search,
  X,
  Save,
} from "lucide-react";
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Node,
  Edge,
  Connection,
  ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import { toast } from "@components/ui/use-toast";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchConfigPage } from "../plugin/createPluginApi";
import {
  saveWorkflows,
  getAllWorkflows,
  getWorkflowById,
  deleteWorkflow,
  executeWorkflow,
} from "./createPluginApis";

import CreateMeshFlowDialog from "./CreateMeshFlowDialog";
import Confirmation from "@components/common/ConfirmDialog";
import MeshflowSidebar from "./MeshflowSidebar";
import { useNavigate } from "react-router-dom";
import { createNode } from "./common/nodeFactory";
import { nodeConfigs } from "./common/nodeConfigs";
import {
  customNodes,
  dataTransmission,
  logicNodes,
} from "./common/nodeTemplates";
import RequestBlock from "./nodes/RequestBlock";
import IfElseNode from "./nodes/IfElseNode";
import SwitchNode from "./nodes/SwitchNode";
import ConfigutrationNode from "./nodes/ConfigurationNode";
import ExportedMeshflowNode from "./nodes/ExportedMeshflowNode";
import ConnectFlowNode from "./nodes/ConnectFlowNode";
import PageLoader from "@components/loaders/PageLoader";
import EditFieldsNode from "./nodes/EditFieldsNode";
import SaveLogsNode from "./nodes/SaveLogsNode";
import SaveDatalistNode from "./nodes/SaveDatalistNode";
import SplitOutNode from "./nodes/SplitOutNode";
import AggregateNode from "./nodes/AggregateNode";
import MergeNode from "./nodes/MergeNode";
import StartNode from "./nodes/StartNode";
import SortNode from "./nodes/SortNode";
import LimitNode from "./nodes/LimitNode";
import PythonCodeNode from "./nodes/PythonCodeNode";
import LoopOverItemsNode from "./nodes/LoopOverItemsNode";
import AddButtonNode from "./nodes/AddButtonNode";
import StopAndErrorNode from "./nodes/StopAndErrorNode";
import FallBackUi from "../plugin/FallBackUi";
import CustomEdge from "./common/CustomEdge";
import LoopEdge from "./common/LoopEdge";
import {
  transformBackendNodeToReactFlow,
  transformNodeToBackend,
} from "./utils/nodeTransformUtils";
import { getConnectedInputs } from "./common/nodeDataFlow";
import FunctionNode from "./nodes/FunctionNode";
import NodeSearchDialog from "./NodeSearchDialog";

// Initial nodes and edges
const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

// Generate unique ID for new nodes based on node title + epoch
const getId = (title: string = "") => {
  const epoch = Date.now();
  const sanitizedTitle =
    title.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase() || "node";
  return `${sanitizedTitle}_${epoch}`;
};

// Helper function to clean up backend-formatted nodes (remove fieldsToInclude arrays, etc.)
const cleanBackendNode = (node: any): any => {
  // Don't process if node doesn't have parameters
  if (!node || !node.parameters) return node;

  // Deep clone to avoid any reference issues
  const cleanedNode = JSON.parse(JSON.stringify(node));
  const params = cleanedNode.parameters;

  // Ensure label is always set to the node's header name
  // Import getDefaultNodeLabel from utils to get the proper display name
  if (cleanedNode.sub_type && !cleanedNode.label) {
    // Get the default label based on sub_type (e.g., "HTTP Request" for "http_request")
    const getDefaultNodeLabel = (subType: string): string => {
      if (subType === "manual_trigger" || subType === "start") return "Start";
      if (subType === "http_request") return "HTTP Request";
      if (subType === "if_else") return "If Block";
      if (subType === "switch" || subType === "switch_case") return "Switch";
      if (subType === "edit_fields" || subType === "set_node")
        return "Edit Fields";
      if (subType === "split_out") return "Split Out";
      if (subType === "aggregate") return "Aggregate";
      if (subType === "merge_items" || subType === "merge") return "Merge";
      if (subType === "loop_over_items") return "Loop Over Items";
      if (subType === "sort_node" || subType === "sort") return "Sort";
      if (subType === "limit_node" || subType === "limit") return "Limit";
      if (subType === "save_to_logs") return "Save Logs";
      if (subType === "save__to_parquet") return "Save Logs";
      if (subType === "save_to_datalist") return "Save Datalist";
      if (subType === "authentication" || subType === "config_node")
        return "Authentication";
      if (subType === "python_code") return "Python Code";
      if (subType === "functional_node_linkshadow") return "Function";
      if (subType === "stop_and_error") return "Stop and Error";
      if (subType === "sub_workflow_node") return "Connect Flow";
      return "Node";
    };
    cleanedNode.label = getDefaultNodeLabel(cleanedNode.sub_type);
  }

  // Clean up split_out nodes - AGGRESSIVE cleanup
  if (cleanedNode.sub_type === "split_out") {
    // For split_out: ONLY keep fieldsToInclude if include is "selectedOtherFields" AND it has a value
    if (params.include === "selectedOtherFields") {
      // Keep fieldsToInclude only if it's a non-empty string
      if (
        !params.fieldsToInclude ||
        Array.isArray(params.fieldsToInclude) ||
        (typeof params.fieldsToInclude === "string" &&
          !params.fieldsToInclude.trim())
      ) {
        // Set to empty string instead of deleting (backend expects it)
        params.fieldsToInclude = "";
      }
    } else {
      // For any other include mode, set to empty string (backend expects field to exist)
      params.fieldsToInclude = "";
    }
  }

  // Clean up aggregate nodes
  if (cleanedNode.sub_type === "aggregate") {
    // Only keep fieldsToInclude if include is "specifiedFields" AND it has a value
    if (params.include === "specifiedFields") {
      if (
        !params.fieldsToInclude ||
        (typeof params.fieldsToInclude === "string" &&
          !params.fieldsToInclude.trim())
      ) {
        params.fieldsToInclude = "";
      }
    } else {
      // For other modes, set to empty string (backend expects field to exist)
      params.fieldsToInclude = "";
    }

    // Only keep fieldsToExclude if include is "allFieldsExcept" AND it has a value
    if (params.include === "allFieldsExcept") {
      if (
        !params.fieldsToExclude ||
        (typeof params.fieldsToExclude === "string" &&
          !params.fieldsToExclude.trim())
      ) {
        params.fieldsToExclude = "";
      }
    } else {
      // For other modes, set to empty string (backend expects field to exist)
      params.fieldsToExclude = "";
    }
  }

  return cleanedNode;
};

// Node types for React Flow
const nodeTypes = {
  authentication: ConfigutrationNode,
  exportedMeshflow: ExportedMeshflowNode,
  connectFlow: ConnectFlowNode,
  ifElseNode: IfElseNode,
  switchNode: SwitchNode,
  requestBlock: RequestBlock,
  editFields: EditFieldsNode,
  splitOutNode: SplitOutNode,
  aggregateNode: AggregateNode,
  mergeNode: MergeNode,
  sortNode: SortNode,
  limitNode: LimitNode,
  functionNode: FunctionNode,
  savelogs: SaveLogsNode,
  savedatalist: SaveDatalistNode,
  startNode: StartNode,
  pythonCodeNode: PythonCodeNode,
  stopAndErrorNode: StopAndErrorNode,
  loopOverItemsNode: LoopOverItemsNode,
  addButtonNode: AddButtonNode,
};

// Edge types for React Flow
const edgeTypes = {
  custom: CustomEdge,
  loop: LoopEdge,
};

const CreatePluginNode: React.FC = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);
  const stableNodeTypes = React.useMemo(() => nodeTypes, []);
  const stableEdgeTypes = React.useMemo(() => edgeTypes, []);
  const stableDefaultEdgeOptions = React.useMemo(
    () => ({ type: "", animated: true }),
    [],
  );

  // Get pluginId from sessionStorage reactively
  const [pluginId, setPluginId] = useState<string>(() => {
    return (
      sessionStorage.getItem("meshPluginId") ||
      sessionStorage.getItem("editPluginId") ||
      ""
    );
  });

  // Update pluginId if sessionStorage changes
  React.useEffect(() => {
    const newPluginId =
      sessionStorage.getItem("meshPluginId") ||
      sessionStorage.getItem("editPluginId") ||
      "";
    if (newPluginId !== pluginId) {
      setPluginId(newPluginId);
    }
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Track if we're currently loading a workflow to prevent false unsaved changes
  const isLoadingWorkflowRef = useRef(false);

  // Custom edge change handler to clear node data when edges are removed
  const handleEdgesChange = useCallback(
    (changes: any[]) => {
      // Check for edge removals
      const removedEdges = changes.filter((change) => change.type === "remove");

      if (removedEdges.length > 0) {
        removedEdges.forEach((change) => {
          const edgeId = change.id;
          const edge = edges.find((e) => e.id === edgeId);

          if (edge) {
            // Clear input data for target node
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === edge.target) {
                  // Clear input-related data from target node
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      inputData: [],
                    },
                  };
                }
                return node;
              }),
            );
          }
        });
      }

      // Call the original edges change handler
      onEdgesChange(changes);
    },
    [edges, onEdgesChange, setNodes],
  );
  const [exportedMeshflows, setExportedMeshflows] = React.useState<any[]>([]);
  const [showDeleteNodeDialog, setShowDeleteNodeDialog] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);
  const [showDeleteMeshFlowDialog, setShowDeleteMeshFlowDialog] =
    useState(false);
  const [meshFlowToDelete, setMeshFlowToDelete] = useState<string | null>(null);
  const [showCreateMeshFlowDialog, setShowCreateMeshFlowDialog] =
    useState(false);
  const [showNodeSearchDialog, setShowNodeSearchDialog] = useState(false);
  const [activeMeshFlow, setActiveMeshFlow] = React.useState<any>(null);
  const [meshFlows, setMeshFlows] = React.useState<any[]>([]);
  const [activeMeshFlowUid, setActiveMeshFlowUid] = React.useState<
    string | null
  >(null);
  const [editingMeshFlow, setEditingMeshFlow] = React.useState<any>(null);
  const [loadingWorkflowUid, setLoadingWorkflowUid] = React.useState<
    string | null
  >(null);
  const [isExecutingWorkflow, setIsExecutingWorkflow] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    type: "back" | "switch" | "deselect" | "backToUIPages";
    data?: any;
  } | null>(null);
  const saveAndExitToAiRef = useRef(false);

  // Keep session storage in sync with active flow metadata/state so node-level
  // step execution always uses the latest workflow payload fields.
  React.useEffect(() => {
    if (activeMeshFlow) {
      sessionStorage.setItem("activeMeshFlow", JSON.stringify(activeMeshFlow));
    }
  }, [activeMeshFlow]);

  const navigate = useNavigate();

  React.useEffect(() => {
    const meshPluginId = sessionStorage.getItem("meshPluginId");
    const editPluginId = sessionStorage.getItem("editPluginId");
    const hasSessionPluginId = !!(meshPluginId || editPluginId);
    const resolvedPluginId = meshPluginId || editPluginId || pluginId;

    if (!hasSessionPluginId || !resolvedPluginId) {
      sessionStorage.removeItem("activeMeshFlow");
      navigate("/ai-engine", { replace: true });
    }
  }, [pluginId, navigate]);

  // Handle Ctrl+Space keyboard shortcut to open node search dialog
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.code === "Space") {
        event.preventDefault();
        setShowNodeSearchDialog(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle Ctrl+S keyboard shortcut to save mesh flow
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        event.code === "KeyS"
      ) {
        event.preventDefault();
        handleSaveMeshflow();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pluginId, activeMeshFlow, nodes, edges, meshFlows, activeMeshFlowUid]);

  // Handle Ctrl+Shift+E keyboard shortcut to execute mesh flow
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.code === "KeyE"
      ) {
        event.preventDefault();
        if (activeMeshFlow) {
          handleRunMeshFlow();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pluginId, activeMeshFlow, nodes]);

  // Handle browser back button - navigate to ai-engine
  React.useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();

      // Check for unsaved changes
      if (hasUnsavedChanges && activeMeshFlow) {
        setPendingNavigation({ type: "back" });
        setShowSaveConfirmDialog(true);
        // Push state back to prevent navigation
        window.history.pushState(null, "", window.location.pathname);
      } else {
        // Keep meshPluginId and editPluginId to preserve plugin creation context
        // Only clear active mesh flow data
        sessionStorage.removeItem("activeMeshFlow");
        navigate("/ai-engine", { replace: true });
      }
    };

    // Push initial state to enable back button interception
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, hasUnsavedChanges, activeMeshFlow]);

  // Cleanup sessionStorage on page unload
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Show browser confirmation if there are unsaved changes
      if (hasUnsavedChanges && activeMeshFlow) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }

      // Keep meshPluginId and editPluginId on reload to preserve plugin creation state
      // Only clear active mesh flow data
      sessionStorage.removeItem("activeMeshFlow");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, activeMeshFlow]);

  // Validate pluginId on mount
  React.useEffect(() => {
    if (!pluginId || pluginId === "temp-plugin") {
      console.warn(
        " Plugin ID not found or is temporary. Please save the plugin first.",
      );
      toast({
        variant: "destructive",
        title: " Plugin Not Found.",
        description:
          "Plugin ID is missing. To continue, create a new plugin or open the flow of an existing plugin.",
        duration: 5000,
      });
    }
  }, []);

  // Reset state when pluginId changes (navigating to a different plugin)
  React.useEffect(() => {
    // Clear active mesh flow state
    setActiveMeshFlow(null);
    setActiveMeshFlowUid(null);

    // Clear nodes and edges
    setNodes([]);
    setEdges([]);

    // Clear unsaved changes flag
    setHasUnsavedChanges(false);

    // Reset loading states
    setLoadingWorkflowUid(null);
    setIsExecutingWorkflow(false);
  }, [pluginId]);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  const transformConnectionsToEdges = React.useCallback(
    (connections: any, nodesMap?: Map<string, Node>) => {
      if (!connections) return [];

      const edges: Edge[] = [];

      Object.entries(connections).forEach(
        ([sourceId, connection]: [string, any]) => {
          if (connection.main && Array.isArray(connection.main)) {
            connection.main.forEach(
              (targetArray: any[], outputIndex: number) => {
                targetArray.forEach((target: any) => {
                  let initialItemCount = 0;
                  let sourceHandle: string | undefined = undefined;

                  if (nodesMap) {
                    const sourceNode = nodesMap.get(sourceId);

                    // --- sourceHandle detection ---
                    const isSwitchNode =
                      sourceNode?.type === "switchNode" ||
                      sourceNode?.data?.sub_type === "switch_case";

                    const isIfElseNode =
                      sourceNode?.type === "ifElseNode" ||
                      sourceNode?.data?.sub_type === "if_else";

                    const isLoopNode =
                      sourceNode?.type === "loopOverItemsNode" ||
                      sourceNode?.data?.sub_type === "loop_over_items";

                    if (isSwitchNode) {
                      sourceHandle = `output_${outputIndex}`;
                    } else if (isIfElseNode) {
                      sourceHandle = outputIndex === 1 ? "false" : "true";
                    } else if (isLoopNode) {
                      // main[0] = "done", main[1] = "loop"
                      sourceHandle = outputIndex === 0 ? "done" : "loop";

                      // Skip self-loop edges (handled visually elsewhere)
                      if (target.node === sourceId) {
                        return;
                      }
                    }

                    // Skip loop-back edges (loop node receiving index 1)
                    const targetNode = nodesMap.get(target.node);
                    const isTargetLoopNode =
                      targetNode?.type === "loopOverItemsNode" ||
                      targetNode?.data?.sub_type === "loop_over_items";

                    if (isTargetLoopNode && target.index === 1) {
                      return;
                    }

                    // --- initialItemCount logic ---
                    if (sourceNode?.data?.response) {
                      const response = sourceNode.data.response;
                      initialItemCount = Array.isArray(response)
                        ? response.length
                        : 1;
                    } else if (sourceNode?.data?.outputData) {
                      const outputData = sourceNode.data.outputData;
                      initialItemCount = Array.isArray(outputData)
                        ? outputData.length
                        : 1;
                    } else if (sourceNode?.data?.configData) {
                      initialItemCount = 1;
                    }
                  }

                  // Determine edge type based on source/target node types
                  const sourceNode = nodesMap?.get(sourceId);
                  const targetNode = nodesMap?.get(target.node);
                  const isLoopEdge =
                    sourceNode?.type === "loopOverItemsNode" ||
                    targetNode?.type === "loopOverItemsNode" ||
                    sourceHandle === "loop" ||
                    sourceHandle === "done";

                  const edge: Edge = {
                    id: `edge-${sourceId}-${target.node}-${sourceHandle ?? outputIndex}-${target.index ?? 0}`,
                    source: sourceId,
                    target: target.node,
                    type: isLoopEdge ? "loop" : "custom",
                    animated: true,
                    data: {
                      itemCount: initialItemCount,
                      isLoopBack:
                        sourceHandle === "output" &&
                        targetNode?.type === "loopOverItemsNode" &&
                        target.index === 1,
                    },
                    style: {
                      strokeWidth: 2,
                      strokeDasharray: "5 3",
                    },
                  };

                  if (sourceHandle) {
                    edge.sourceHandle = sourceHandle;
                  }

                  // Convert 0-based index → 1-based handle
                  if (target.index !== undefined && target.index >= 0) {
                    edge.targetHandle = `input-${target.index + 1}`;
                  }

                  edges.push(edge);
                });
              },
            );
          }
        },
      );

      return edges;
    },
    [],
  );

  // Mutation for saving workflows
  const saveWorkflowMutation = useMutation(
    (payload: { workflowData: any[]; is_edit: boolean }) => {
      return saveWorkflows(payload, pluginId);
    },
    {
      onSuccess: () => {
        toast({
          variant: "success",
          title: "Workflow Saved!",
          description: "Your mesh flow has been saved successfully.",
          duration: 3000,
        });

        // Clear unsaved changes flag
        setHasUnsavedChanges(false);

        // Refetch workflows after successful save
        queryClient.invalidateQueries(["getAllWorkflows", pluginId]);

        if (saveAndExitToAiRef.current) {
          saveAndExitToAiRef.current = false;
          toast({
            variant: "success",
            title: "Success",
            description: "Plugin has been saved successfully.",
            duration: 2000,
          });
          sessionStorage.removeItem("activeMeshFlow");
          navigate("/ai-engine", { replace: true });
          return;
        }

        // Handle pending navigation after save
        if (pendingNavigation) {
          handlePendingNavigation();
        }
      },
      onError: (error: any) => {
        if (saveAndExitToAiRef.current) {
          saveAndExitToAiRef.current = false;
        }

        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save workflow. Please try again.";

        // Check for plugin ID errors
        if (
          errorMessage.toLowerCase().includes("plugin") &&
          (errorMessage.toLowerCase().includes("not found") ||
            errorMessage.toLowerCase().includes("invalid"))
        ) {
          toast({
            variant: "destructive",
            title: "Plugin Not Found",
            description:
              "The plugin ID is invalid or missing. Please save your plugin first and try again.",
            duration: 5000,
          });
          return;
        }

        // Don't show error toast for "Workflow already exists" - let the caller handle retry
        if (
          errorMessage.includes("Workflow already exists") ||
          errorMessage.includes("already exists")
        ) {
          return; // Skip showing error toast, the specific mutate call will handle retry
        }

        toast({
          variant: "destructive",
          title: "Error Saving Workflow",
          description: errorMessage,
          duration: 5000,
        });
      },
    },
  );

  // Mutation for fetching workflow by ID
  const fetchWorkflowMutation = useMutation(
    ({ workflowId, pluginId }: { workflowId: string; pluginId: string }) => {
      return getWorkflowById(workflowId, pluginId);
    },
    {
      onSuccess: (data) => {
        setLoadingWorkflowUid(null);
        if (data?.data) {
          // Set loading flag to prevent hasUnsavedChanges from being set during load
          isLoadingWorkflowRef.current = true;
          const workflow = data.data;

          // Transform nodes and edges
          const transformedNodes = (workflow.nodes || [])
            .map((node: any) =>
              transformBackendNodeToReactFlow(node, {
                onDelete: handleDeleteNode,
                onDataChange: handleNodeDataChange,
              }),
            )
            .map((node: Node) => {
              // Update configuration nodes with fresh API data ONLY - don't use saved workflow data
              if (node.type === "authentication") {
                // Extract UID from saved workflow parameters (not configData)
                const savedParams = node.data?.parameters;
                const nodeConfigUid = savedParams?.Uid || savedParams?.uid;
                const configTitle =
                  savedParams?.title || node.data?.header || "Configuration";

                // Always look up fresh configuration data from API by UID
                const freshConfig = configurations.find(
                  (config) => config.uid === nodeConfigUid,
                );

                if (freshConfig) {
                  // Configuration exists in API - use fresh data only
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      pluginId,
                      configurations,
                      configData: freshConfig, // Use fresh API data
                      header: freshConfig.title, // Update header with current title
                      lastUpdated: Date.now(), // Force re-render
                      isDeleted: false, // Mark as not deleted
                    },
                  };
                } else if (nodeConfigUid) {
                  // Configuration not found in API (deleted from UI Pages or doesn't exist)
                  // Clear configData and mark as deleted - don't use old saved data
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      pluginId,
                      configurations,
                      configData: null, // Clear saved data - show empty state
                      header: configTitle, // Keep original title for reference
                      lastUpdated: Date.now(), // Force re-render
                      isDeleted: true, // Mark as deleted to show fallback message
                    },
                  };
                }
              }
              return node;
            });

          // Create a map of nodes for edge transformation
          const nodesMap = new Map<string, Node>(
            transformedNodes.map((node: Node) => [node.id, node]),
          );

          const transformedEdges = transformConnectionsToEdges(
            workflow.connections,
            nodesMap,
          ).filter((edge: Edge) => {
            // Double-check: filter out edges connected to deleted configuration nodes
            return nodesMap.has(edge.source) && nodesMap.has(edge.target);
          });

          // Update active mesh flow with transformed data
          const meshFlowData = {
            uid: workflow.uid,
            title: workflow.title,
            description: workflow.description,
            type_workflow: workflow.type_workflow,
            version: workflow.version,
            is_active: workflow.is_active || false,
            is_export: workflow.is_export || false,
            cron_run_time: workflow.cron_run_time,
            dependency_workflow: workflow.dependency_workflow || [],
            threat_lookup_search_types:
              workflow.threat_lookup_search_types || [],
            threatlookup_data: workflow.threatlookup_data || {},
            workflow_priority: workflow.workflow_priority || "",
            nodes: transformedNodes,
            edges: transformedEdges,
            connections: workflow.connections || {}, // Preserve original connections from backend
          };

          setActiveMeshFlow(meshFlowData);
          setActiveMeshFlowUid(meshFlowData.uid);
          sessionStorage.setItem(
            "activeMeshFlow",
            JSON.stringify(meshFlowData),
          );

          // Update meshFlows array to include this workflow's data with connections
          setMeshFlows((prevFlows) =>
            prevFlows.map((flow) =>
              flow.uid === workflow.uid
                ? {
                    ...flow,
                    threat_lookup_search_types:
                      workflow.threat_lookup_search_types || [],
                    threatlookup_data: workflow.threatlookup_data || {},
                    nodes: transformedNodes,
                    edges: transformedEdges,
                    connections: workflow.connections || {},
                  }
                : flow,
            ),
          );

          // Load nodes and edges to canvas
          // Ensure there's always a start node - add one if missing
          const hasStartNode = transformedNodes.some(
            (node: Node) => node.type === "startNode",
          );
          let finalNodes = transformedNodes;

          if (!hasStartNode) {
            // Auto-add start node if it doesn't exist
            const startNodeId = getId("start");
            const startNode: Node = {
              id: startNodeId,
              type: "startNode",
              position: { x: 100, y: 250 },
              data: {
                uid: startNodeId,
                title: "Start",
                label: "Start",
                header: "Start",
                borderColor: "var(--node-start)",
                shadowColor: "var(--node-shadow-start)",
                iconStyle: "text-muted-foreground",
                onDelete: handleDeleteNode,
                onDataChange: handleNodeDataChange,
                response: [{}],
                outputData: [{}],
              },
            };
            finalNodes = [startNode, ...transformedNodes];
          }

          // Fix bad backend positions (negative or off-screen coordinates)
          // Only fix positions that are clearly invalid (0,0 or backend's bad default)
          finalNodes = finalNodes.map((node: Node, index: number) => {
            // Check if position is invalid (0,0 or backend's specific bad default range)
            // Backend sometimes returns positions around (-1833, -62) for new workflows
            // Allow user-positioned negative coordinates (user can drag nodes left/up from origin)
            // AFTER (tightened to only the actual backend glitch range ~-1833):
            const isBadBackendDefault =
              (node.position.x < -1800 && node.position.x > -1870) ||
              (node.position.y < -50 && node.position.y > -80);

            if (
              (node.position.x === 0 && node.position.y === 0) ||
              isBadBackendDefault
            ) {
              // Set reasonable default position based on index
              if (node.type === "startNode") {
                return {
                  ...node,
                  position: { x: 100, y: 250 },
                };
              } else {
                // Position nodes horizontally with spacing
                return {
                  ...node,
                  position: { x: 100 + index * 300, y: 250 },
                };
              }
            }
            return node;
          });

          // Re-create AddButtonNodes for any Loop Over Items nodes
          const addButtonNodesToCreate: Node[] = [];
          const loopEdgesToCreate: Edge[] = [];

          (finalNodes as Node[]).forEach((node) => {
            if (node.type === "loopOverItemsNode") {
              // Check connections object for loop-back edges (index: 1)
              // Find nodes that connect back to this loop with index: 1
              const loopBackSources: string[] = [];

              if (workflow.connections) {
                Object.entries(workflow.connections).forEach(
                  ([sourceId, conn]: [string, any]) => {
                    if (conn.main && Array.isArray(conn.main)) {
                      conn.main.forEach((targets: any[]) => {
                        if (Array.isArray(targets)) {
                          targets.forEach((target: any) => {
                            if (target.node === node.id && target.index === 1) {
                              // This source node connects back to loop's index 1 (loop input)
                              loopBackSources.push(sourceId);
                            }
                          });
                        }
                      });
                    }
                  },
                );
              }

              if (loopBackSources.length > 0) {
                // Get the last node that loops back
                const lastNodeId = loopBackSources[0]; // Usually only one loop-back source
                const lastNode = (finalNodes as Node[]).find(
                  (n) => n.id === lastNodeId,
                );

                // Count nodes in the loop chain for proper positioning
                const countNodesInChain = (loopNodeId: string): number => {
                  let count = 0;
                  const visited = new Set<string>();

                  // Start from nodes connected to loop's "loop" output (main[1])
                  const loopConnection = workflow.connections?.[loopNodeId];
                  if (!loopConnection?.main?.[1]) return 0;

                  const firstTargets = loopConnection.main[1];
                  if (!Array.isArray(firstTargets) || firstTargets.length === 0)
                    return 0;

                  let currentId = firstTargets[0].node;

                  while (
                    currentId &&
                    !visited.has(currentId) &&
                    currentId !== loopNodeId
                  ) {
                    visited.add(currentId);
                    count++;

                    const nextConn = workflow.connections?.[currentId];
                    if (!nextConn?.main?.[0]?.length) break;

                    const nextTarget = nextConn.main[0].find(
                      (t: any) => t.node !== loopNodeId,
                    );
                    if (!nextTarget) break;

                    currentId = nextTarget.node;
                  }

                  return count;
                };

                const nodeCount = countNodesInChain(node.id);

                // Create AddButtonNode with proper loop positioning
                const addButtonNodeId = `addButton-${node.id}`;

                // Use the same positioning logic as handleAddNodeToLoop for consistency
                const buttonPosition = lastNode
                  ? {
                      x: lastNode.position.x + -200, // To the left of last node
                      y: node.position.y + 300 + nodeCount * 20, // Progressive downward
                    }
                  : {
                      x: node.position.x + 250,
                      y: node.position.y + 300,
                    };

                const addButtonNode = createNode({
                  id: addButtonNodeId,
                  type: "addButtonNode",
                  position: buttonPosition,
                  header: "Add",
                  borderColor: "var(--node-loop-over)",
                  shadowColor: "var(--node-loop-over-shadow)",
                  extraData: {
                    uid: addButtonNodeId,
                    loopNodeId: node.id,
                    onAddNode: (nodeType: string) => {
                      handleAddNodeToLoop(nodeType, addButtonNodeId, node.id);
                    },
                    onDelete: handleDeleteNode,
                  },
                  onDelete: handleDeleteNode,
                  onDataChange: handleNodeDataChange,
                });

                addButtonNodesToCreate.push(addButtonNode);

                // Create loop edges with improved styling
                if (lastNodeId) {
                  // Calculate itemCount from lastNode's data
                  let itemCount = 0;
                  if (lastNode?.data?.response) {
                    const response = lastNode.data.response;
                    itemCount = Array.isArray(response) ? response.length : 1;
                  } else if (lastNode?.data?.outputData) {
                    const outputData = lastNode.data.outputData;
                    itemCount = Array.isArray(outputData)
                      ? outputData.length
                      : 1;
                  } else if (lastNode?.data?.configData) {
                    itemCount = 1;
                  }

                  // Edge from lastNode to AddButton (part of loop flow)
                  loopEdgesToCreate.push({
                    id: `edge-${lastNodeId}-${addButtonNodeId}`,
                    source: lastNodeId,
                    target: addButtonNodeId,
                    targetHandle: "input",
                    type: "loop",
                    animated: true,
                    data: { itemCount, isLoopBack: false },
                    style: { strokeWidth: 2, strokeDasharray: "5 3" },
                  } as Edge);

                  // Edge from AddButton back to Loop (loop-back edge - special styling)
                  loopEdgesToCreate.push({
                    id: `edge-${addButtonNodeId}-${node.id}`,
                    source: addButtonNodeId,
                    target: node.id,
                    targetHandle: "input", // Connect to loop node's input
                    type: "loop",
                    animated: true,
                    data: { itemCount, isLoopBack: true },
                    style: { strokeWidth: 2.5, strokeDasharray: "8 4" },
                  } as Edge);
                }
              }
            }
          });

          // Add the AddButtonNodes to finalNodes
          if (addButtonNodesToCreate.length > 0) {
            finalNodes = [...finalNodes, ...addButtonNodesToCreate];
          }

          // Add loop edges to transformedEdges
          let finalEdges = transformedEdges;
          if (loopEdgesToCreate.length > 0) {
            finalEdges = [...transformedEdges, ...loopEdgesToCreate];
          }

          setNodes(finalNodes);
          setEdges(finalEdges);

          // Clear unsaved changes flag when loading a workflow
          setHasUnsavedChanges(false);

          // Clear loading flag after a delay to ensure all effects have run
          // This needs to be long enough for edge-fixing and label-update effects to complete
          setTimeout(() => {
            isLoadingWorkflowRef.current = false;
          }, 500);

          // Fit view to show all nodes properly after a brief delay
          setTimeout(() => {
            if (reactFlowInstance) {
              reactFlowInstance.fitView({ padding: 0.1, maxZoom: 1 });
            }
          }, 100);

          toast({
            variant: "success",
            title: `Mesh Flow "${workflow.title}" Activated!`,
            description: `Now working on "${workflow.title}".`,
            duration: 3000,
          });
        }
      },
      onError: (error: any) => {
        setLoadingWorkflowUid(null);
        // Clear active state on error
        setActiveMeshFlowUid(null);

        // Log detailed error for debugging
        console.error("Error loading workflow:", {
          error,
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });

        // Provide more specific error messages
        let errorMessage = "Failed to load workflow. Please try again.";

        if (error?.response?.status === 404) {
          errorMessage = "Workflow not found. It may have been deleted.";
        } else if (error?.response?.status === 403) {
          errorMessage = "You don't have permission to access this workflow.";
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        toast({
          variant: "destructive",
          title: "Error Loading Workflow",
          description: errorMessage,
          duration: 4000,
        });
      },
    },
  );

  // Mutation for deleting workflow by ID
  const deleteWorkflowMutation = useMutation(
    ({ workflowId, pluginId }: { workflowId: string; pluginId: string }) => {
      return deleteWorkflow(workflowId, pluginId);
    },
    {
      onSuccess: (_response, variables) => {
        // Clear active state if the deleted workflow was active
        if (activeMeshFlowUid === variables.workflowId) {
          setActiveMeshFlow(null);
          setActiveMeshFlowUid(null);
          setNodes([]);
          setEdges([]);
        }

        toast({
          variant: "success",
          title: "Mesh Flow Deleted",
          description: "The mesh flow has been deleted successfully.",
          duration: 3000,
        });

        // Refetch workflows to update the list
        queryClient.invalidateQueries(["getAllWorkflows", pluginId]);
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error Deleting Workflow",
          description:
            error?.message || "Failed to delete workflow. Please try again.",
          duration: 3000,
        });
      },
    },
  );

  // Helper function to unwrap json_data from workflow response
  // Uses deep comparison and caching to preserve references when content is identical
  const unwrapCacheRef = useRef<Map<string, any>>(new Map());

  const unwrapJsonData = useCallback((data: any): any => {
    if (!data) return data;

    // Create a stable key for caching based on content, not reference
    const dataKey = JSON.stringify(data);

    // Check cache first - if we've seen this exact content before, return cached result
    if (unwrapCacheRef.current.has(dataKey)) {
      return unwrapCacheRef.current.get(dataKey);
    }

    let result: any;

    if (Array.isArray(data)) {
      result = data.map((item) => {
        // Check if item has json_data wrapper structure
        if (
          item &&
          typeof item === "object" &&
          "json_data" in item &&
          Object.keys(item).length <= 4
        ) {
          return item.json_data;
        }
        return item;
      });
    } else if (
      data &&
      typeof data === "object" &&
      "json_data" in data &&
      Object.keys(data).length <= 4
    ) {
      // Single object with json_data
      result = data.json_data;
    } else {
      result = data;
    }

    // Cache the result using content-based key
    unwrapCacheRef.current.set(dataKey, result);

    // Limit cache size to prevent memory leaks
    if (unwrapCacheRef.current.size > 50) {
      const firstKey = unwrapCacheRef.current.keys().next().value;
      if (firstKey !== undefined) {
        unwrapCacheRef.current.delete(firstKey);
      }
    }

    return result;
  }, []);

  // Helper function to find all downstream nodes recursively
  const findDownstreamNodes = useCallback(
    (nodeId: string, edges: Edge[]): Set<string> => {
      const downstream = new Set<string>();
      const queue = [nodeId];
      const visited = new Set<string>();

      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        // Find all edges where current node is the source
        const outgoingEdges = edges.filter((e) => e.source === currentId);

        outgoingEdges.forEach((edge) => {
          downstream.add(edge.target);
          queue.push(edge.target);
        });
      }

      return downstream;
    },
    [],
  );

  // Mutation for executing workflow
  const executeWorkflowMutation = useMutation(
    (payload: any) => {
      return executeWorkflow(payload);
    },
    {
      onMutate: (payload: any) => {
        setIsExecutingWorkflow(true);
        // Clear unwrap cache to prevent stale references
        unwrapCacheRef.current.clear();

        const executingNodeUids = new Set<string>(
          (payload?.workflowData?.nodes || [])
            .map((node: any) => node?.uid)
            .filter(Boolean),
        );

        // Mark only nodes included in execution payload as pending.
        setNodes((nds) =>
          nds.map((node) => {
            const nodeUid = node.data?.uid || node.id;
            if (!executingNodeUids.has(nodeUid)) {
              return node;
            }

            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
              },
            };
          }),
        );
      },
      onSuccess: async (response: any) => {
        // Get node results from the response
        const results = response?.results?.[0];
        const nodeResults = results?.node_results || [];

        // Check if there are node results to process
        const hasNodeResults = nodeResults.length > 0;

        if (hasNodeResults) {
          // Build a map of node results for quick lookup
          const resultsMap = new Map<string, any>();
          nodeResults.forEach((result: any) => {
            resultsMap.set(result.uid, result);
          });

          // Find failed nodes and their downstream nodes
          const failedNodeIds = new Set<string>();
          const downstreamOfFailedNodes = new Set<string>();
          let firstErrorMessage = "";
          let firstErrorNodeTitle = "";

          nodeResults.forEach((result: any) => {
            // Backend can return "error", "failed", or "failure" status
            if (
              result.status === "error" ||
              result.status === "failed" ||
              result.status === "failure"
            ) {
              failedNodeIds.add(result.uid);

              // Capture first error message for toast
              if (!firstErrorMessage && result.error) {
                const errorArray = Array.isArray(result.error)
                  ? result.error
                  : [result.error];
                const firstError = errorArray[0];
                firstErrorMessage =
                  firstError?.message || firstError || "Unknown error";
                firstErrorNodeTitle = result.title || "Unknown Node";
              }

              // Find all downstream nodes
              const downstream = findDownstreamNodes(result.uid, edges);
              downstream.forEach((nodeId) =>
                downstreamOfFailedNodes.add(nodeId),
              );
            }
          });

          // Determine if workflow execution is complete success or has errors
          const hasErrors = failedNodeIds.size > 0;

          // Update all nodes at once with their results AND inputData - single render
          setNodes((nds) => {
            // First, update nodes with their execution results
            const updatedNodes = nds.map((node) => {
              // Match results using the same UID logic used when building the workflow
              // Backend uses node.data.uid if present, otherwise node.id
              const nodeUid = node.data.uid || node.id;
              const result = resultsMap.get(nodeUid) as any;

              // Handle configuration/authentication nodes
              if (node.data.configData) {
                if (result) {
                  // Backend may return "error", "failed", "success", or "completed"
                  // Normalize to either "success" or "failed" for UI consistency
                  let status =
                    result.status === "success" || result.status === "completed"
                      ? "success"
                      : "failed";

                  // If this config node failed or is downstream of a failed node, mark as failed
                  if (
                    failedNodeIds.has(nodeUid) ||
                    downstreamOfFailedNodes.has(nodeUid)
                  ) {
                    status = "failed";
                  }

                  return {
                    ...node,
                    data: {
                      ...node.data,
                      executionStatus: status,
                      executionResult: result,
                      error: result.error,
                      lastUpdated: Date.now(),
                    },
                  };
                } else if (downstreamOfFailedNodes.has(nodeUid)) {
                  // No result but downstream of failed node - mark as failed
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      executionStatus: "failed",
                      error: "Not executed due to upstream failure",
                    },
                  };
                } else {
                  // No result for this config node, clear pending status
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      executionStatus: undefined,
                      executionResult: undefined,
                    },
                  };
                }
              }

              // Handle regular workflow nodes
              if (result) {
                // Backend may return "error", "failed", "success", or "completed"
                // Normalize to either "success" or "failed" for UI consistency
                let status =
                  result.status === "success" || result.status === "completed"
                    ? "success"
                    : "failed";

                // If this node failed or is downstream of a failed node, mark as failed
                if (
                  failedNodeIds.has(nodeUid) ||
                  downstreamOfFailedNodes.has(nodeUid)
                ) {
                  status = "failed";
                }

                // Unwrap json_data from the output - always create fresh data
                const unwrappedData = unwrapJsonData(result.output_data);

                // Always create a new reference to force React to detect the change
                // Deep clone to ensure no reference reuse issues
                const freshData = JSON.parse(JSON.stringify(unwrappedData));

                // Update execution status and data with timestamp to force re-render
                // Calculate if_else node_data from raw output_data
                const ifElseNodeData = (() => {
                  if (
                    node.type !== "ifElseNode" &&
                    node.data?.sub_type !== "if_else"
                  )
                    return undefined;

                  const rawOutput = result.output_data;
                  if (!rawOutput || !Array.isArray(rawOutput)) return undefined;

                  // Backend returns [[trueItems...], [falseItems...]]
                  const trueItems = Array.isArray(rawOutput[0])
                    ? rawOutput[0]
                    : [];
                  const falseItems = Array.isArray(rawOutput[1])
                    ? rawOutput[1]
                    : [];

                  return [
                    {
                      id: "true",
                      outputKey: "True",
                      itemCount: trueItems.length,
                    },
                    {
                      id: "false",
                      outputKey: "False",
                      itemCount: falseItems.length,
                    },
                  ];
                })();

                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: status,
                    executionResult: result,
                    response: freshData,
                    outputData: freshData,
                    executionTime: result.execution_time,
                    error: result.error,
                    lastUpdated: Date.now(),
                    is_loop_child: result.is_loop_child || false,
                    parent_loop_node: result.parent_loop_node || null,
                    total_iterations: result.total_iterations || 0,
                    iterations: result.iterations || [],
                    // Update node_data for if_else nodes so edge item counts update
                    ...(ifElseNodeData && { node_data: ifElseNodeData }),
                  },
                };
              } else if (
                node.data.executionStatus === "pending" ||
                downstreamOfFailedNodes.has(nodeUid)
              ) {
                // Node was marked as pending but didn't execute
                // OR is downstream of a failed node - mark as failed
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: downstreamOfFailedNodes.has(nodeUid)
                      ? "failed"
                      : undefined,
                    executionResult: undefined,
                    error: downstreamOfFailedNodes.has(nodeUid)
                      ? "Not executed due to upstream failure"
                      : undefined,
                  },
                };
              }
              return node;
            });

            // Then, update inputData ONLY for nodes that didn't just execute
            // This prevents overwriting the inputData that was used during execution
            return updatedNodes.map((node) => {
              // Skip config nodes
              if (node.data.configData) return node;

              // Skip nodes that just executed - they should keep their execution-time inputData
              // Use the same UID logic for consistency
              const nodeUid = node.data.uid || node.id;
              const nodeJustExecuted = resultsMap.has(nodeUid);
              if (nodeJustExecuted) return node;

              // Get input data from connected nodes using the updated nodes
              const connectedInputs = getConnectedInputs(
                node.id,
                edges,
                updatedNodes,
              );

              // Update inputData if there are connected inputs
              if (connectedInputs.length > 0) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    inputData: connectedInputs,
                  },
                };
              }
              return node;
            });
          });

          // Update edges with itemCount based on execution results
          setEdges((eds) => {
            return eds.map((edge) => {
              // Skip edges from nodes that manage their own edge counts (have node_data)
              // This includes Switch and IfElse nodes
              const sourceNode = nodes.find((n) => n.id === edge.source);
              if (
                sourceNode &&
                sourceNode.data.node_data &&
                sourceNode.data.node_data.length > 0
              ) {
                // Node manages its own edge counts, don't override
                return edge;
              }

              let sourceResult;

              // Special handling for AddButton nodes - they don't execute, so get count from incoming edge
              if (sourceNode?.type === "addButtonNode") {
                const incomingEdge = eds.find((e) => e.target === edge.source);
                if (incomingEdge) {
                  // Get the result from the node that feeds into AddButton
                  sourceResult = resultsMap.get(incomingEdge.source);
                }
              } else {
                // Normal case: match edge.source to result.uid directly
                sourceResult = resultsMap.get(edge.source);
              }

              if (sourceResult && sourceResult.output_data) {
                const unwrappedData = unwrapJsonData(sourceResult.output_data);
                const itemCount = Array.isArray(unwrappedData)
                  ? unwrappedData.length
                  : 1;

                return {
                  ...edge,
                  data: {
                    ...edge.data,
                    itemCount,
                    lastUpdated: Date.now(), // Force update detection
                  },
                };
              }
              return edge;
            });
          });

          // All done - final cleanup
          setIsExecutingWorkflow(false);

          // Show appropriate toast based on execution result
          if (hasErrors) {
            // Show error toast with specific error message
            const errorDescription = firstErrorMessage
              ? `${firstErrorNodeTitle}: ${firstErrorMessage}`
              : "One or more nodes failed during execution";

            const shortError =
              errorDescription.length > 150
                ? errorDescription.substring(0, 150) + "..."
                : errorDescription;

            toast({
              variant: "destructive",
              title: "Workflow Execution Failed",
              description: shortError,
              duration: 7000,
            });
          } else {
            // Only show success if ALL nodes succeeded
            toast({
              variant: "success",
              title: "Workflow Executed successfully!",
              duration: 3000,
            });
          }
        } else {
          // Fallback for responses without node_results
          setIsExecutingWorkflow(false);

          // Check if the overall response indicates an error
          const isError =
            response.success === false || response.status === "error";

          if (isError) {
            // Extract error message
            const errorMsg =
              response.error ||
              response.message ||
              "An error occurred during workflow execution";
            const shortError =
              errorMsg.length > 150
                ? errorMsg.substring(0, 150) + "..."
                : errorMsg;

            // Show error to user
            toast({
              variant: "destructive",
              title: "Workflow Execution Failed",
              description: shortError,
              duration: 7000,
            });

            // Update nodes to show failed state
            setNodes((nds) =>
              nds.map((node) => ({
                ...node,
                data: {
                  ...node.data,
                  executionStatus: "failed",
                  error: errorMsg,
                },
              })),
            );
            return;
          }

          // Unwrap json_data from response if present
          const unwrappedResponse = unwrapJsonData(response.data);

          setNodes((nds) =>
            nds.map((node) => {
              // For config nodes, mark as success
              if (node.data.configData) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                    executionResult: { status: "success" },
                    lastUpdated: Date.now(),
                  },
                };
              }
              // For workflow nodes, set success status with response data
              return {
                ...node,
                data: {
                  ...node.data,
                  executionStatus: "success",
                  response: unwrappedResponse,
                  outputData: unwrappedResponse,
                },
              };
            }),
          );

          // Update edges with itemCount
          setEdges((eds) =>
            eds.map((edge) => {
              const itemCount = Array.isArray(unwrappedResponse)
                ? unwrappedResponse.length
                : 1;
              return {
                ...edge,
                data: {
                  ...edge.data,
                  itemCount,
                },
              };
            }),
          );

          // Update all nodes' inputData from their connected predecessors
          setNodes((nds) =>
            nds.map((node) => {
              // Skip config nodes
              if (node.data.configData) return node;

              // Get input data from connected nodes
              const connectedInputs = getConnectedInputs(node.id, edges, nds);

              // Only update if node has connected inputs
              if (connectedInputs.length > 0) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    inputData: connectedInputs,
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Workflow Executed successfully!",
            duration: 3000,
          });
        }
      },
      onError: (error: any) => {
        setIsExecutingWorkflow(false);

        // Check for validation errors
        if (error?.response?.data?.details) {
          const validationMsg = `Validation failed: ${error.response.data.details.length} error(s)`;
          toast({
            variant: "destructive",
            title: "Workflow Validation Failed",
            description: validationMsg,
            duration: 7000,
          });
        } else {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to execute workflow. Please try again.";

          // Check for plugin ID errors
          if (
            errorMessage.toLowerCase().includes("plugin") &&
            (errorMessage.toLowerCase().includes("not found") ||
              errorMessage.toLowerCase().includes("invalid") ||
              errorMessage.toLowerCase().includes("id"))
          ) {
            toast({
              variant: "destructive",
              title: "Plugin Not Found",
              description:
                "The plugin ID is invalid or missing. Please save your plugin first and try again.",
              duration: 5000,
            });
          } else {
            toast({
              variant: "destructive",
              title: "Execution Failed",
              description: errorMessage,
              duration: 5000,
            });
          }
        }

        // Mark nodes with failed status
        setNodes((nds) =>
          nds.map((node) => {
            // Mark all nodes (including config nodes) with failed status
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.response?.data?.message || error?.message,
              },
            };
          }),
        );
      },
    },
  );

  // Transform edges to connections schema
  const transformEdgesToConnections = (
    edges: Edge[],
    executableNodes?: Node[],
  ) => {
    const connections: Record<
      string,
      { main: Array<Array<{ node: string; type: string; index: number }>> }
    > = {};

    const nodesToUse = executableNodes || nodes;
    const nodeMap = new Map(nodesToUse.map((n) => [n.id, n]));

    // Create a mapping from React Flow IDs to UIDs
    const idToUidMap: Record<string, string> = {};
    nodesToUse.forEach((node) => {
      idToUidMap[node.id] = node.data?.uid || node.id;
    });

    const validNodeIds = executableNodes
      ? new Set(executableNodes.map((n) => n.id))
      : null;

    const mergeNodeInputs: Record<string, number> = {};

    edges.forEach((edge) => {
      // --- Filter invalid edges (execution mode) ---
      if (
        validNodeIds &&
        (!validNodeIds.has(edge.source) || !validNodeIds.has(edge.target))
      ) {
        // Special case: AddButton -> LoopNode edge
        if (edge.target && validNodeIds?.has(edge.target)) {
          const targetNode = nodeMap.get(edge.target);

          const isTargetLoopNode =
            targetNode?.type === "loopOverItemsNode" ||
            targetNode?.data?.sub_type === "loop_over_items";

          if (isTargetLoopNode) {
            const incomingEdge = edges.find(
              (e) => e.target === edge.source && validNodeIds?.has(e.source),
            );

            if (incomingEdge) {
              const realSourceId = incomingEdge.source;
              const realSourceUid = idToUidMap[realSourceId];
              const targetUid = idToUidMap[edge.target];

              if (!connections[realSourceUid]) {
                connections[realSourceUid] = { main: [] };
              }

              if (!connections[realSourceUid].main[0]) {
                connections[realSourceUid].main[0] = [];
              }

              const alreadyExists = connections[realSourceUid].main[0].some(
                (conn: any) => conn.node === targetUid,
              );

              if (!alreadyExists) {
                connections[realSourceUid].main[0].push({
                  node: targetUid,
                  type: "main",
                  index: 1,
                });
              }
            }
          }
        }
        return;
      }

      // Get UIDs for source and target
      const sourceUid = idToUidMap[edge.source];
      const targetUid = idToUidMap[edge.target];

      // --- Ensure source exists ---
      if (!connections[sourceUid]) {
        connections[sourceUid] = { main: [] };
      }

      let outputIndex = 0;
      const sourceNode = nodeMap.get(edge.source);

      // --- Node type detection ---
      const isSwitchNode =
        sourceNode?.type === "switchNode" ||
        sourceNode?.data?.sub_type === "switch_case";

      const isIfElseNode =
        sourceNode?.type === "ifElseNode" ||
        sourceNode?.data?.sub_type === "if_else";

      const isLoopNode =
        sourceNode?.type === "loopOverItemsNode" ||
        sourceNode?.data?.sub_type === "loop_over_items";

      // --- Determine outputIndex from sourceHandle ---
      if (isSwitchNode && edge.sourceHandle) {
        if (edge.sourceHandle.startsWith("output_")) {
          const parsed = parseInt(edge.sourceHandle.replace("output_", ""), 10);
          outputIndex = isNaN(parsed) ? 0 : parsed;
        }
      } else if (isIfElseNode && edge.sourceHandle) {
        outputIndex = edge.sourceHandle === "false" ? 1 : 0;
      } else if (isLoopNode && edge.sourceHandle) {
        // "done" -> 0, "loop" -> 1
        outputIndex = edge.sourceHandle === "loop" ? 1 : 0;
      }

      // --- Ensure array exists safely ---
      while (connections[sourceUid].main.length <= outputIndex) {
        connections[sourceUid].main.push([]);
      }

      // --- Determine target index ---
      let targetIndex = 0;

      if (edge.targetHandle) {
        const match = edge.targetHandle.match(/input-(\d+)/);
        if (match) {
          targetIndex = parseInt(match[1], 10) - 1;
        }
      } else {
        const targetNode = nodeMap.get(edge.target);

        if (targetNode && targetNode.type === "mergeNode") {
          const targetUid = idToUidMap[edge.target];
          if (!(targetUid in mergeNodeInputs)) {
            mergeNodeInputs[targetUid] = 0;
          }

          targetIndex = mergeNodeInputs[targetUid];
          mergeNodeInputs[targetUid]++;
        }
      }

      connections[sourceUid].main[outputIndex].push({
        node: targetUid,
        type: "main",
        index: targetIndex,
      });
    });

    // --- Ensure loop nodes always ունեն 2 outputs ---
    executableNodes?.forEach((node) => {
      const nodeUid = node.data?.uid || node.id;

      const isLoopNode =
        node.type === "loopOverItemsNode" ||
        node.data?.sub_type === "loop_over_items";

      if (isLoopNode) {
        if (!connections[nodeUid]) {
          connections[nodeUid] = { main: [[], []] };
        }

        if (!connections[nodeUid].main[0]) {
          connections[nodeUid].main[0] = [];
        }

        if (!connections[nodeUid].main[1]) {
          connections[nodeUid].main[1] = [];
        }
      }
    });

    return connections;
  };

  // Fetch configurations from API
  const { data: configPageData, isLoading: isLoadingConfig } = useQuery({
    queryKey: ["fetchConfigPage", pluginId, "temp"],
    queryFn: () => fetchConfigPage(pluginId!, "temp"),
    enabled: !!pluginId,
    staleTime: 0,
    cacheTime: 0, // Don't cache - always fetch fresh data
    refetchInterval: 5000, // Refetch every 5 seconds to catch updates
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });

  // Fetch all workflows from API
  const { data: workflowsData, isLoading: isLoadingWorkflows } = useQuery({
    queryKey: ["getAllWorkflows", pluginId],
    queryFn: () => getAllWorkflows(pluginId),
    enabled: !!pluginId,
    staleTime: 0,
  });

  // Extract configurations from API response
  const configurations = React.useMemo(() => {
    if (!configPageData?.data) return [];

    // The data is structured as { "timestamp": [ ...configs... ] }
    // Get the first key's value (the configurations array)
    const dataKeys = Object.keys(configPageData.data);
    if (dataKeys.length === 0) return [];

    const tabId = dataKeys[0]; // The key is the tabId
    const configArray = configPageData.data[tabId];

    // Add tabId, type, and configType to each config object
    return Array.isArray(configArray)
      ? configArray.map((config) => ({
          ...config,
          tabId,
          type: "temp", // Match what HTTP nodes save
          configType: "temp", // Keep for backward compatibility
        }))
      : [];
  }, [configPageData]);

  // Update configuration nodes when API data changes
  // Clear contents if configurations are deleted from UI Pages, but keep the node
  React.useEffect(() => {
    if (!configurations) return;

    // Track which configuration nodes had their data cleared
    const nodesClearedCount = { count: 0 };
    const nodesRestoredCount = { count: 0 };

    setNodes((nds) => {
      const updatedNodes = nds.map((node) => {
        // Only process authentication/configuration nodes
        if (node.type !== "authentication") return node;

        // Find matching configuration by uid - check both configData and parameters
        const nodeConfigUid =
          node.data?.configData?.uid ||
          node.data?.parameters?.Uid ||
          node.data?.parameters?.uid;

        const matchingConfig = configurations.find(
          (config) => config.uid === nodeConfigUid,
        );

        if (matchingConfig) {
          // Check if configData has actually changed to avoid unnecessary re-renders
          const configChanged =
            JSON.stringify(node.data?.configData) !==
            JSON.stringify(matchingConfig);

          const wasDeleted = node.data?.isDeleted === true;

          if (configChanged || wasDeleted) {
            // Update the node's configData with fresh data from API
            // Also restore if it was previously deleted
            if (wasDeleted) {
              nodesRestoredCount.count++;
            }

            return {
              ...node,
              data: {
                ...node.data,
                configData: matchingConfig,
                header: matchingConfig.title, // Update header in case title changed
                lastUpdated: Date.now(), // Force re-render with timestamp
                isDeleted: false, // Clear deleted flag if it was set
              },
            };
          }
          return node; // No change needed
        } else if (nodeConfigUid) {
          // Configuration no longer exists in API - clear the contents but keep the node
          const wasNotDeleted = node.data?.isDeleted !== true;
          if (wasNotDeleted) {
            nodesClearedCount.count++;
          }

          return {
            ...node,
            data: {
              ...node.data,
              configData: null, // Clear the configuration data
              header: node.data?.header || "Deleted Configuration", // Keep original header or show deleted
              lastUpdated: Date.now(), // Force re-render
              isDeleted: true, // Mark as deleted for visual indication
            },
          };
        }
        return node;
      });

      return updatedNodes;
    });

    // Show notification if configurations were cleared
    if (nodesClearedCount.count > 0) {
      toast({
        variant: "default",
        title: "Configuration Cleared",
        description: `${nodesClearedCount.count} configuration node(s) cleared as they were deleted from UI Pages. The node(s) remain in the flow.`,
        duration: 4000,
      });
    }

    // Show notification if configurations were restored
    if (nodesRestoredCount.count > 0) {
      toast({
        variant: "success",
        title: "Configuration Restored",
        description: `${nodesRestoredCount.count} configuration node(s) restored with updated data from UI Pages.`,
        duration: 4000,
      });
    }
  }, [configurations, setNodes]);

  const [edgeMenu, setEdgeMenu] = useState<{
    edgeId: string;
    x: number;
    y: number;
  } | null>(null);

  // Close edge context menu on click outside
  React.useEffect(() => {
    const close = () => setEdgeMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  // Handle right-click on edges to show context menu
  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault();

      setEdgeMenu({
        edgeId: edge.id,
        x: event.clientX,
        y: event.clientY,
      });
    },
    [],
  );

  // Handle connecting nodes with moving dashed edges
  const onConnect = useCallback(
    (params: Edge | Connection) => {
      if (!isLoadingWorkflowRef.current) {
        setHasUnsavedChanges(true);
      }

      // Check if source node has response data to set initial itemCount
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);
      let initialItemCount = 0;

      // For nodes with node_data (IfElse, Switch), get itemCount from the specific handle
      if (sourceNode?.data?.node_data && params.sourceHandle) {
        const handleData = sourceNode.data.node_data.find(
          (item: any) => item.id === params.sourceHandle,
        );
        if (handleData && handleData.itemCount !== undefined) {
          initialItemCount = handleData.itemCount;
        }
      } else if (sourceNode?.data?.response) {
        const response = sourceNode.data.response;
        initialItemCount = Array.isArray(response) ? response.length : 1;
      } else if (sourceNode?.data?.outputData) {
        const outputData = sourceNode.data.outputData;
        initialItemCount = Array.isArray(outputData) ? outputData.length : 1;
      } else if (sourceNode?.data?.configData) {
        // Configuration nodes provide a single config object
        initialItemCount = 1;
      }

      // Determine edge type based on whether source or target is a loop node
      const isLoopEdge =
        sourceNode?.type === "loopOverItemsNode" ||
        targetNode?.type === "loopOverItemsNode" ||
        params.sourceHandle === "loop" ||
        params.sourceHandle === "done";

      setEdges((eds: Edge[]) =>
        addEdge(
          {
            ...params,
            type: isLoopEdge ? "loop" : "custom",
            animated: true,
            data: {
              itemCount: initialItemCount,
            },
            style: {
              strokeWidth: 2,
              strokeDasharray: "5 3",
            },
          },
          eds,
        ),
      );

      // Check if target is a Loop Over Items node and create auto-loop with AddButtonNode
      if (targetNode?.type === "loopOverItemsNode") {
        setTimeout(() => {
          // Check if AddButtonNode already exists for this loop
          const existingAddButton = nodes.find(
            (n) =>
              n.type === "addButtonNode" && n.data.loopNodeId === params.target,
          );

          if (!existingAddButton) {
            // Get the position of the loop node
            const loopNode = nodes.find((n) => n.id === params.target);
            if (!loopNode) return;

            // Create AddButtonNode positioned to the bottom right of the loop node
            const addButtonNodeId = `addButton-${params.target}`;
            const addButtonNode = createNode({
              id: addButtonNodeId,
              type: "addButtonNode",
              position: {
                x: loopNode.position.x + 20,
                y: loopNode.position.y + 250,
              },
              header: "Add",
              borderColor: "var(--node-loop-over)",
              shadowColor: "var(--node-loop-over-shadow)",
              extraData: {
                uid: addButtonNodeId,
                loopNodeId: params.target,
                onAddNode: (nodeType: string) => {
                  handleAddNodeToLoop(
                    nodeType,
                    addButtonNodeId,
                    params.target as string,
                  );
                },
                onDelete: handleDeleteNode,
              },
              onDelete: handleDeleteNode,
              onDataChange: handleNodeDataChange,
            });

            setNodes((nds) => [...nds, addButtonNode]);

            // Create edges: LoopNode(loop) -> AddButtonNode(input) and AddButtonNode(output) -> LoopNode(input)
            setTimeout(() => {
              setEdges((eds) => {
                const edge1: Edge = {
                  id: `loop-to-addbtn-${Date.now()}`,
                  source: params.target as string,
                  target: addButtonNodeId,
                  sourceHandle: "loop",
                  targetHandle: "input",
                  type: "loop",
                  animated: true,
                  data: { itemCount: 0, isLoopBack: false },
                  style: {
                    strokeWidth: 2,
                    strokeDasharray: "5 3",
                  },
                };

                const edge2: Edge = {
                  id: `addbtn-to-loop-${Date.now()}`,
                  source: addButtonNodeId,
                  target: params.target as string,
                  sourceHandle: "output",
                  targetHandle: "input",
                  type: "loop",
                  animated: true,
                  data: { itemCount: 0, isLoopBack: true },
                  style: {
                    strokeWidth: 2.5,
                    strokeDasharray: "8 4",
                  },
                };

                return [...eds, edge1, edge2];
              });
            }, 50);
          }
        }, 100);
      }
    },
    [setEdges, nodes],
  );

  // Handle drag start from sidebar
  const onDragStart = useCallback((event: React.DragEvent, node: any) => {
    event.dataTransfer.setData("application/reactflow-type", node.type);
    event.dataTransfer.setData("application/reactflow-header", node.header);
    event.dataTransfer.setData(
      "application/reactflow-border",
      node.borderColor,
    );
    event.dataTransfer.setData(
      "application/reactflow-shadow",
      node.shadowColor,
    );
    // Pass config data for authentication nodes
    if (node.configData) {
      event.dataTransfer.setData(
        "application/reactflow-configdata",
        JSON.stringify(node.configData),
      );
    }
    event.dataTransfer.effectAllowed = "move";
  }, []);

  // Handle drag over the canvas
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  // Handle drop onto canvas
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!reactFlowInstance) return;

      const type = event.dataTransfer.getData("application/reactflow-type");
      if (type) {
        // Check if there's an active mesh flow before allowing node drop
        if (!activeMeshFlow) {
          toast({
            variant: "destructive",
            title: "No Active Mesh Flow",
            description:
              "Please create a new mesh flow or select an existing one.",
            duration: 3000,
          });
          return;
        }

        // Check if workflow is threatlookup and prevent Save Logs/Save to Datalist nodes
        const workflowType = activeMeshFlow.type_workflow;
        const isThreatLookup =
          workflowType === "threatlookup" || workflowType === "threat_lookup";
        const isRestrictedNode = type === "savelogs" || type === "savedatalist";

        if (isThreatLookup && isRestrictedNode) {
          const nodeName =
            type === "savelogs" ? "Save Logs" : "Save to Datalist";
          toast({
            variant: "destructive",
            title: "Node Not Allowed",
            description: `${nodeName} nodes are not allowed in Threat Lookup workflows.`,
            duration: 4000,
          });
          return;
        }

        const header = event.dataTransfer.getData(
          "application/reactflow-header",
        );
        const borderColor = event.dataTransfer.getData(
          "application/reactflow-border",
        );
        const shadowColor = event.dataTransfer.getData(
          "application/reactflow-shadow",
        );
        const iconStyle = event.dataTransfer.getData(
          "application/reactflow-icon-style",
        );
        const configDataStr = event.dataTransfer.getData(
          "application/reactflow-configdata",
        );

        const flowPosition = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        const config = nodeConfigs[type] || { extraData: {} };

        // Parse config data if available (for authentication nodes)
        const extraData = { ...config.extraData };
        if (configDataStr) {
          try {
            extraData.configData = JSON.parse(configDataStr);
          } catch (e) {
            // Failed to parse config data
          }
        }

        const nodeId = getId(header);
        const newNode = createNode({
          id: nodeId,
          type,
          position: flowPosition,
          header,
          borderColor,
          shadowColor,
          iconStyle,
          extraData: {
            ...extraData,
            uid: nodeId, // Set uid same as node id for consistency
          },
          onDelete: handleDeleteNode,
          onDataChange: handleNodeDataChange,
        });

        setNodes((nds) => [...nds, newNode]);
        setHasUnsavedChanges(true); // Mark as unsaved when node is added
        return; // done with normal nodes
      }

      // --- Exported meshflows ---
      const meshflowData = event.dataTransfer.getData(
        "application/meshflow-data",
      );
      if (meshflowData) {
        const flowData = JSON.parse(meshflowData);

        // Validate: Don't allow dropping an exported workflow into itself
        if (activeMeshFlow && flowData.uid === activeMeshFlow.uid) {
          toast({
            variant: "destructive",
            title: "Cannot Add to Same Workflow",
            description: `You cannot add "${flowData.title}" to itself. Please select a different workflow.`,
            duration: 3000,
          });
          return;
        }

        // Validate: Don't allow the same exported node to be dropped multiple times
        const isDuplicate = nodes.some(
          (node) => node.data?.exportedFlowData?.uid === flowData.uid,
        );

        if (isDuplicate) {
          toast({
            variant: "destructive",
            title: "Duplicate Exported Node",
            description: `"${flowData.title}" is already present in this workflow.`,
            duration: 3000,
          });
          return;
        }

        // Create a single node representing the exported meshflow
        const nodeId = getId(flowData.title || "exported_flow");
        const newNode: Node = {
          id: nodeId,
          type: "exportedMeshflow",
          position: reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          }),
          data: {
            uid: nodeId,
            title: flowData.title || "Exported Flow",
            header: flowData.title || "Exported Flow",
            label: "Exported MeshFlow",
            borderColor: "var(--node-export)",
            shadowColor: "var(--node-shadow-export)",
            iconStyle: "text-var(--node-export)",
            onDelete: handleDeleteNode,
            onDataChange: handleNodeDataChange,
            exportedFlowData: flowData,
            isExportedMeshflow: true,
            onRefresh: handleRefreshExportedNode,
            configData: {
              uid: flowData.uid,
              title: flowData.title,
              description: flowData.description || "Exported MeshFlow",
              type: "exported_meshflow",
              workflow_type: flowData.type_workflow,
              version: flowData.version,
              node_count: flowData.nodes?.length || 0,
              edge_count: flowData.edges?.length || 0,
            },
          },
        };

        setNodes((nds) => [...nds, newNode]);
        setHasUnsavedChanges(true);

        toast({
          variant: "success",
          title: "Exported Flow Added",
          description: `"${flowData.title}" has been added to the canvas.`,
          duration: 2000,
        });
      }
    },
    [reactFlowInstance, activeMeshFlow, nodes],
  );

  // Helper function to sort nodes in topological order (execution order) based on connections
  const sortNodesTopologically = useCallback(
    (nodes: any[], connections: any) => {
      const nodeMap = new Map(nodes.map((n) => [n.uid, n]));
      const visited = new Set<string>();
      const sorted: any[] = [];

      const visit = (uid: string) => {
        if (visited.has(uid)) return;
        visited.add(uid);

        // Add this node to sorted list first
        const node = nodeMap.get(uid);
        if (node) {
          sorted.push(node);
        }

        // Then visit all nodes this one connects to (targets)
        const nodeConnections = connections[uid];
        if (nodeConnections?.main) {
          nodeConnections.main.forEach((outputArray: any[]) => {
            outputArray.forEach((conn: any) => {
              if (nodeMap.has(conn.node)) {
                visit(conn.node);
              }
            });
          });
        }
      };

      // Find start nodes (nodes with no incoming connections)
      const hasIncoming = new Set<string>();
      Object.values(connections).forEach((conn: any) => {
        if (conn.main) {
          conn.main.forEach((outputArray: any[]) => {
            outputArray.forEach((c: any) => {
              hasIncoming.add(c.node);
            });
          });
        }
      });

      const startNodes = nodes.filter((n) => !hasIncoming.has(n.uid));

      // Visit from start nodes
      startNodes.forEach((node) => visit(node.uid));

      // Visit any remaining nodes (in case of disconnected nodes)
      nodes.forEach((node) => {
        if (!visited.has(node.uid)) {
          visit(node.uid);
        }
      });

      return sorted;
    },
    [],
  );

  // Helper function to sort connections object keys in topological order
  const sortConnectionsTopologically = useCallback((connections: any) => {
    if (!connections || Object.keys(connections).length === 0)
      return connections;

    const visited = new Set<string>();
    const sortedKeys: string[] = [];

    const visit = (uid: string) => {
      if (visited.has(uid) || !connections[uid]) return;
      visited.add(uid);

      // Add this key to sorted list first
      sortedKeys.push(uid);

      // Then visit all nodes this one connects to (targets)
      const nodeConnections = connections[uid];
      if (nodeConnections?.main) {
        nodeConnections.main.forEach((outputArray: any[]) => {
          outputArray.forEach((conn: any) => {
            if (connections[conn.node]) {
              visit(conn.node);
            }
          });
        });
      }
    };

    // Find start nodes (nodes with no incoming connections)
    const hasIncoming = new Set<string>();
    Object.values(connections).forEach((conn: any) => {
      if (conn.main) {
        conn.main.forEach((outputArray: any[]) => {
          outputArray.forEach((c: any) => {
            hasIncoming.add(c.node);
          });
        });
      }
    });

    const startNodes = Object.keys(connections).filter(
      (uid) => !hasIncoming.has(uid),
    );

    // Visit from start nodes
    startNodes.forEach((uid) => visit(uid));

    // Visit any remaining nodes (in case of disconnected nodes)
    Object.keys(connections).forEach((uid) => {
      if (!visited.has(uid)) {
        visit(uid);
      }
    });

    // Build sorted connections object
    const sortedConnections: any = {};
    sortedKeys.forEach((key) => {
      sortedConnections[key] = connections[key];
    });

    return sortedConnections;
  }, []);

  // Open export dialog and prevent if there are no nodes in the canvas
  const handleSaveMeshflow = () => {
    // Validate pluginId first
    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin before saving the workflow.",
        duration: 4000,
      });
      return false;
    }

    if (!activeMeshFlow) {
      toast({
        variant: "destructive",
        title: "No Active Mesh Flow",
        description: "Please create or select a mesh flow first.",
        duration: 3000,
      });
      return false;
    }

    if (nodes.length === 0) {
      toast({
        variant: "destructive",
        title: "Nothing to save",
        description: "Add nodes to your meshflow before saving.",
        duration: 3000,
      });
      return false;
    }

    // Include ALL nodes (workflow nodes and configuration nodes)
    // BUT exclude addButtonNode - it's a UI helper only, not a real workflow node
    // Keep configuration nodes even if deleted from UI Pages (configData will be null)
    const workflowNodes = nodes.filter((node) => {
      // Filter out addButtonNode only
      if (node.type === "addButtonNode") return false;

      // Keep all other nodes, including configuration nodes even if deleted
      return true;
    });

    // Transform current active workflow's nodes and edges
    const transformedNodes = workflowNodes.map((node) =>
      transformNodeToBackend(node, pluginId),
    );

    const connections = transformEdgesToConnections(edges, workflowNodes);

    // Sort connections object keys in topological order
    const sortedConnections = sortConnectionsTopologically(connections);

    // Sort nodes in topological order (execution order) based on connections
    const sortedTransformedNodes = sortNodesTopologically(
      transformedNodes,
      connections,
    );

    // Build all workflows array - update active workflow with current canvas data
    const allWorkflowsData = meshFlows.map((flow) => {
      try {
        if (flow.uid === activeMeshFlowUid) {
          // This is the active workflow, use current canvas data
          return {
            uid: flow.uid,
            title: flow.title,
            ...(flow.description && { description: flow.description }),
            type_workflow: flow.type_workflow,
            version: flow.version,
            is_active: flow.is_active || false,
            cron_run_time:
              flow.cron_run_time ||
              (["save_log", "save_datalist", "itdr", "dspm"].includes(
                flow.type_workflow,
              )
                ? "5"
                : null),
            dependency_workflow: flow.dependency_workflow || [],
            threat_lookup_search_types: flow.threat_lookup_search_types || [],
            threatlookup_data: flow.threatlookup_data || {},
            workflow_priority: flow.workflow_priority || "",
            is_export: flow.is_export || false,
            nodes: sortedTransformedNodes,
            connections: sortedConnections,
          };
        } else {
          // Transform stored nodes - check if they're in React Flow format or backend format
          const inactiveNodes = Array.isArray(flow.nodes) ? flow.nodes : [];
          // Check if nodes are in React Flow format (have 'id' and 'data' instead of 'uid' and 'parameters')
          if (
            inactiveNodes.length > 0 &&
            inactiveNodes[0].id &&
            inactiveNodes[0].data
          ) {
            // Filter out addButtonNode from inactive nodes
            const filteredInactiveNodes = inactiveNodes.filter(
              (node: any) => node.type !== "addButtonNode",
            );

            const inactiveEdges = flow.edges || [];
            const inactiveConnections =
              inactiveEdges.length > 0
                ? transformEdgesToConnections(
                    inactiveEdges,
                    filteredInactiveNodes,
                  )
                : flow.connections || {};

            // Sort inactive connections in topological order
            const sortedInactiveConnections =
              sortConnectionsTopologically(inactiveConnections);

            // Now transform nodes to backend format AFTER connections are created
            const transformedInactiveNodes = filteredInactiveNodes.map(
              transformNodeToBackend,
            );

            // Sort inactive nodes in topological order
            const sortedInactiveNodes = sortNodesTopologically(
              transformedInactiveNodes,
              inactiveConnections,
            );

            return {
              uid: flow.uid,
              title: flow.title,
              ...(flow.description && { description: flow.description }),
              type_workflow: flow.type_workflow,
              version: flow.version,
              is_active: flow.is_active || false,
              cron_run_time:
                flow.cron_run_time ||
                (["save_log", "save_datalist", "itdr", "dspm"].includes(
                  flow.type_workflow,
                )
                  ? "5"
                  : null),
              dependency_workflow: flow.dependency_workflow || [],
              threat_lookup_search_types: flow.threat_lookup_search_types || [],
              threatlookup_data: flow.threatlookup_data || {},
              workflow_priority: flow.workflow_priority || "",
              is_export: flow.is_export || false,
              nodes: sortedInactiveNodes,
              connections: sortedInactiveConnections,
            };
          } else {
            // Nodes are already in backend format
            // Always preserve existing connections from flow
            const inactiveConnections = flow.connections || {};

            // Clean up nodes to remove problematic fields (like fieldsToInclude arrays)
            const cleanedNodes = inactiveNodes.map(cleanBackendNode);

            return {
              uid: flow.uid,
              title: flow.title,
              ...(flow.description && { description: flow.description }),
              type_workflow: flow.type_workflow,
              version: flow.version,
              is_active: flow.is_active || false,
              cron_run_time:
                flow.cron_run_time ||
                (["save_log", "save_datalist", "itdr", "dspm"].includes(
                  flow.type_workflow,
                )
                  ? "5"
                  : null),
              dependency_workflow: flow.dependency_workflow || [],
              threat_lookup_search_types: flow.threat_lookup_search_types || [],
              threatlookup_data: flow.threatlookup_data || {},
              workflow_priority: flow.workflow_priority || "",
              is_export: flow.is_export || false,
              nodes: cleanedNodes,
              connections: inactiveConnections,
            };
          }
        }
      } catch (error) {
        // Return minimal structure to prevent complete failure
        return {
          uid: flow.uid,
          title: flow.title,
          type_workflow: flow.type_workflow,
          version: flow.version,
          is_active: false,
          threat_lookup_search_types: flow.threat_lookup_search_types || [],
          nodes: [],
          connections: {},
        };
      }
    });

    if (allWorkflowsData.length === 0) {
      toast({
        variant: "destructive",
        title: "Cannot Save",
        description:
          "No workflows found to save. Please try refreshing the page.",
        duration: 3000,
      });
      return false;
    }

    // Final cleanup: ensure all nodes are clean before sending to backend
    // This catches any backend-formatted nodes that might have slipped through
    const cleanedWorkflowsData = allWorkflowsData.map((workflow) => ({
      ...workflow,
      nodes: workflow.nodes.map((node: any) => {
        // Always clean nodes with parameters (backend format)
        if (node.parameters) {
          const cleaned = cleanBackendNode(node);
          // Double-check: ensure fieldsToInclude is completely removed for split_out with wrong mode
          if (
            cleaned.sub_type === "split_out" &&
            cleaned.parameters &&
            cleaned.parameters.include !== "selectedOtherFields" &&
            "fieldsToInclude" in cleaned.parameters
          ) {
            delete cleaned.parameters.fieldsToInclude;
          }
          return cleaned;
        }
        // Otherwise return as-is (already transformed)
        return node;
      }),
    }));

    // ABSOLUTE FINAL SAFETY CHECK - inline cleanup right before sending
    // This ensures fieldsToInclude arrays are converted to empty strings (backend expects field to exist)
    const finalCleanedData = JSON.parse(JSON.stringify(cleanedWorkflowsData));
    finalCleanedData.forEach((workflow: any) => {
      workflow.nodes?.forEach((node: any) => {
        if (node.parameters) {
          // Clean up split_out nodes - set to empty string instead of delete
          if (node.sub_type === "split_out") {
            if (Array.isArray(node.parameters.fieldsToInclude)) {
              node.parameters.fieldsToInclude = "";
            } else if (
              node.parameters.include !== "selectedOtherFields" &&
              "fieldsToInclude" in node.parameters
            ) {
              node.parameters.fieldsToInclude = "";
            } else if (!("fieldsToInclude" in node.parameters)) {
              // Always add the field if it doesn't exist
              node.parameters.fieldsToInclude = "";
            }
          }
          // Clean up aggregate nodes - set to empty string instead of delete
          if (node.sub_type === "aggregate") {
            if (node.parameters.include !== "specifiedFields") {
              node.parameters.fieldsToInclude = "";
            } else if (!("fieldsToInclude" in node.parameters)) {
              node.parameters.fieldsToInclude = "";
            }
            if (node.parameters.include !== "allFieldsExcept") {
              node.parameters.fieldsToExclude = "";
            } else if (!("fieldsToExclude" in node.parameters)) {
              node.parameters.fieldsToExclude = "";
            }
          }
          // Clean up Function nodes - migrate from legacy mappingThreatLookup to mappingDataList
          if (node.sub_type === "functional_node_linkshadow") {
            // If using legacy field name, migrate to new field name
            if (
              node.parameters.mappingThreatLookup &&
              !node.parameters.mappingDataList
            ) {
              node.parameters.mappingDataList =
                node.parameters.mappingThreatLookup;
            }
            // Remove legacy field
            delete node.parameters.mappingThreatLookup;
            // Ensure mappingDataList exists (even if empty array for non-threat-lookup actions)
            if (!node.parameters.mappingDataList) {
              node.parameters.mappingDataList = [];
            }
          }
        }
      });
    });

    const workflowPayload = {
      workflowData: finalCleanedData,
      is_edit: true,
    };

    saveWorkflowMutation.mutate(workflowPayload);
    return true;
  };

  const handleSaveAndGoToAiEngine = () => {
    if (saveWorkflowMutation.isLoading || isExecutingWorkflow) return;
    saveAndExitToAiRef.current = true;
    const saveStarted = handleSaveMeshflow();
    if (!saveStarted) {
      saveAndExitToAiRef.current = false;
      return;
    }
  };

  // Handle Ctrl+Shift+S keyboard shortcut to save and navigate to AI Engine
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.code === "KeyS"
      ) {
        event.preventDefault();
        if (activeMeshFlow) {
          handleSaveAndGoToAiEngine();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeMeshFlow, saveWorkflowMutation.isLoading, isExecutingWorkflow]);

  // Handle export mesh flow
  const handleExportFlow = () => {
    if (!activeMeshFlow) {
      toast({
        variant: "destructive",
        title: "No Active Mesh Flow",
        description: "Please select a mesh flow to export.",
        duration: 3000,
      });
      return;
    }

    if (nodes.length === 0) {
      toast({
        variant: "destructive",
        title: "Nothing to Export",
        description: "Add nodes to your meshflow before exporting.",
        duration: 3000,
      });
      return;
    }

    // First, save the workflow
    handleSaveMeshflow();

    // Wait a bit for save to complete, then export
    setTimeout(() => {
      // Create the exported node data with full flow information
      const exportedNode = {
        header: activeMeshFlow.title,
        icon: "save",
        type: "exportedMeshflow",
        borderColor: "var(--node-config)",
        shadowColor: "var(--node-shadow-config)",
        flowData: {
          uid: activeMeshFlow.uid,
          title: activeMeshFlow.title,
          description: activeMeshFlow.description,
          type_workflow: activeMeshFlow.type_workflow,
          version: activeMeshFlow.version,
          nodes: nodes.filter((n) => n.type !== "addButtonNode"),
          edges: edges,
          connections: activeMeshFlow.connections,
        },
      };

      // Check if this flow is already exported (by uid)
      const existingIndex = exportedMeshflows.findIndex(
        (m: any) => m.flowData?.uid === activeMeshFlow.uid,
      );

      let updatedExportedMeshflows;
      let isUpdate = false;

      if (existingIndex !== -1) {
        // Update existing exported flow
        updatedExportedMeshflows = exportedMeshflows.map(
          (m: any, idx: number) => (idx === existingIndex ? exportedNode : m),
        );
        isUpdate = true;
      } else {
        // Add new exported flow
        updatedExportedMeshflows = [...exportedMeshflows, exportedNode];
      }

      setExportedMeshflows(updatedExportedMeshflows);

      // Save to localStorage
      localStorage.setItem(
        "meshflow-data",
        JSON.stringify(updatedExportedMeshflows),
      );

      toast({
        variant: "success",
        title: isUpdate ? "MeshFlow Updated!" : "MeshFlow Exported!",
        description: isUpdate
          ? `"${activeMeshFlow.title}" has been saved and updated in Exported Nodes.`
          : `"${activeMeshFlow.title}" has been saved and added to Exported Nodes.`,
        duration: 3000,
      });
    }, 1000);
  };

  // Handle create new mesh flow
  const handleCreateMeshFlow = (meshflowData: {
    uid: string;
    title: string;
    description?: string;
    type_workflow: string;
    version: string;
    is_active: boolean;
    is_export?: boolean;
    cron_run_time?: string | null;
    dependency_workflow?: string[];
    threat_lookup_search_types: string[];
    threatlookup_data?: {
      search_type: string;
      search_value: string;
    };
    workflow_priority?: string;
  }) => {
    // Generate a more unique UID to avoid collisions
    const uniqueUid = `workflow_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Apply default cron_run_time for scheduled workflows if not provided
    const defaultCronRunTime =
      meshflowData.cron_run_time ||
      (["save_log", "save_datalist", "itdr", "dspm"].includes(
        meshflowData.type_workflow,
      )
        ? "5"
        : null);

    const newMeshFlow = {
      uid: uniqueUid, // Use generated unique UID instead of the one from dialog
      title: meshflowData.title,
      ...(meshflowData.description && {
        description: meshflowData.description,
      }),
      type_workflow: meshflowData.type_workflow,
      version: meshflowData.version,
      is_active: meshflowData.is_active,
      is_export: meshflowData.is_export || false,
      cron_run_time: defaultCronRunTime,
      dependency_workflow: meshflowData.dependency_workflow || [],
      threat_lookup_search_types: meshflowData.threat_lookup_search_types || [],
      threatlookup_data: meshflowData.threatlookup_data || {},
      workflow_priority: meshflowData.workflow_priority || "",
      nodes: [],
      connections: {},
      edges: [],
    };

    setShowCreateMeshFlowDialog(false);

    // Immediately add the new workflow to the local state for instant UI feedback
    const updatedMeshFlows = [...meshFlows, newMeshFlow];
    setMeshFlows(updatedMeshFlows);

    // Set it as active immediately
    setActiveMeshFlow(newMeshFlow);
    setActiveMeshFlowUid(newMeshFlow.uid);

    // Clear canvas - nodes will be loaded from API response
    setNodes([]);
    setEdges([]);

    // Get all existing workflows from backend data or empty array
    // IMPORTANT: Clean all nodes to remove bad data like fieldsToInclude arrays
    const existingBackendWorkflows =
      workflowsData?.data && Array.isArray(workflowsData.data)
        ? workflowsData.data.map((flow: any) => {
            // Clean all nodes from this workflow
            const cleanedNodes = (flow.nodes || []).map((node: any) =>
              cleanBackendNode(node),
            );

            return {
              uid: flow.uid,
              title: flow.title,
              ...(flow.description && { description: flow.description }),
              type_workflow: flow.type_workflow,
              version: flow.version,
              is_active: flow.is_active || false,
              cron_run_time:
                flow.cron_run_time ||
                (["save_log", "save_datalist", "itdr", "dspm"].includes(
                  flow.type_workflow,
                )
                  ? "5"
                  : null),
              dependency_workflow: flow.dependency_workflow || [],
              threat_lookup_search_types: flow.threat_lookup_search_types || [],
              threatlookup_data: flow.threatlookup_data || {},
              nodes: cleanedNodes,
              connections: flow.connections || {},
            };
          })
        : [];

    const hasExistingWorkflows = existingBackendWorkflows.length > 0;
    // Send ALL workflows (existing + new)
    const workflowPayload = {
      workflowData: [
        ...existingBackendWorkflows,
        {
          uid: newMeshFlow.uid,
          title: newMeshFlow.title,
          ...(newMeshFlow.description && {
            description: newMeshFlow.description,
          }),
          type_workflow: newMeshFlow.type_workflow,
          version: newMeshFlow.version,
          is_active: newMeshFlow.is_active,
          is_export: newMeshFlow.is_export || false,
          cron_run_time: newMeshFlow.cron_run_time,
          dependency_workflow: newMeshFlow.dependency_workflow,
          threat_lookup_search_types: newMeshFlow.threat_lookup_search_types,
          threatlookup_data: newMeshFlow.threatlookup_data,
          workflow_priority: newMeshFlow.workflow_priority,
          nodes: [],
          connections: {},
        },
      ],
      is_edit: hasExistingWorkflows, // true if plugin has workflows, false for first workflow
    };

    // Use mutate with onSuccess callback to handle the response
    saveWorkflowMutation.mutate(workflowPayload, {
      onSuccess: async (response) => {
        toast({
          variant: "success",
          title: "Mesh Flow Created!",
          description: `Your mesh flow "${meshflowData.title}" has been created successfully.`,
          duration: 3000,
        });

        // Close the dialog on success
        setShowCreateMeshFlowDialog(false);

        // Load nodes from the API response
        // Handle both response formats: data as array or data.workflows as array
        const workflows = response?.data?.workflows || response?.data;
        if (workflows && Array.isArray(workflows)) {
          // Find the workflow we just created by UID
          const workflow = workflows.find(
            (w: any) => w.uid === newMeshFlow.uid,
          );

          if (!workflow) {
            console.error("Created workflow not found in response");
            return;
          }

          // Transform nodes from backend format to React Flow format
          const transformedNodes = (workflow.nodes || [])
            .map((node: any) => {
              const reactFlowNode = transformBackendNodeToReactFlow(node, {
                onDelete: handleDeleteNode,
                onDataChange: handleNodeDataChange,
              });

              // Fix position only if it's truly invalid (0,0 or backend's bad default)
              // Backend sometimes returns positions around (-1833, -62) for new workflows
              const isBadBackendDefault =
                (reactFlowNode.position.x < -1500 &&
                  reactFlowNode.position.x > -2000) ||
                (reactFlowNode.position.y < -100 &&
                  reactFlowNode.position.y > -200);

              if (
                (reactFlowNode.position.x === 0 &&
                  reactFlowNode.position.y === 0) ||
                isBadBackendDefault
              ) {
                // Set reasonable default position
                if (reactFlowNode.type === "startNode") {
                  reactFlowNode.position = { x: 100, y: 250 };
                } else {
                  reactFlowNode.position = { x: 250, y: 250 };
                }
              }

              return reactFlowNode;
            })
            .map((node: Node) => {
              // Update configuration nodes with fresh API data instead of saved data
              if (node.type === "authentication") {
                const nodeConfigUid = node.data?.configData?.uid;
                const freshConfig = configurations.find(
                  (config) => config.uid === nodeConfigUid,
                );

                if (freshConfig) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      configData: freshConfig, // Use fresh API data
                      header: freshConfig.title, // Update header with current title
                      lastUpdated: Date.now(), // Force re-render
                    },
                  };
                }
              }
              return node;
            });

          // Create a map of nodes for edge transformation

          // Create a map of nodes for edge transformation
          const nodesMap = new Map<string, Node>(
            transformedNodes.map((node: Node) => [node.id, node]),
          );

          const transformedEdges = transformConnectionsToEdges(
            workflow.connections,
            nodesMap,
          );

          // Update active mesh flow with the actual data from backend
          const meshFlowData = {
            uid: workflow.uid,
            title: workflow.title,
            description: workflow.description,
            type_workflow: workflow.type_workflow,
            version: workflow.version,
            is_active: workflow.is_active || false,
            is_export: workflow.is_export || false,
            cron_run_time: workflow.cron_run_time,
            dependency_workflow: workflow.dependency_workflow || [],
            nodes: transformedNodes,
            edges: transformedEdges,
            connections: workflow.connections || {},
          };

          setActiveMeshFlow(meshFlowData);
          setActiveMeshFlowUid(meshFlowData.uid);

          // Load the nodes and edges to canvas
          setNodes(transformedNodes);
          setEdges(transformedEdges);

          // Clear unsaved changes flag for new workflow
          setHasUnsavedChanges(false);

          // Fit view to show all nodes properly after a brief delay
          setTimeout(() => {
            if (reactFlowInstance) {
              reactFlowInstance.fitView({ padding: 0.1, maxZoom: 1 });
            }
          }, 100);
        }

        // Refetch workflows to sync with backend and get any server-assigned values
        queryClient.refetchQueries(["getAllWorkflows", pluginId]);
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create workflow. Please try again.";

        // Check if error is "Workflow already exists" and we used is_edit: false
        // If so, retry with is_edit: true (without showing error toast)
        if (
          errorMessage.includes("Workflow already exists") ||
          errorMessage.includes("already exists")
        ) {
          // Retry with is_edit: true
          const retryPayload = {
            ...workflowPayload,
            is_edit: true,
          };

          saveWorkflowMutation.mutate(retryPayload, {
            onSuccess: async (response) => {
              toast({
                variant: "success",
                title: "Mesh Flow Created!",
                description: `Your mesh flow "${meshflowData.title}" has been created successfully.`,
                duration: 3000,
              });

              setShowCreateMeshFlowDialog(false);

              // Load nodes from the API response
              // Handle both response formats: data as array or data.workflows as array
              const workflows = response?.data?.workflows || response?.data;
              if (workflows && Array.isArray(workflows)) {
                // Find the workflow we just created by UID
                const workflow = workflows.find(
                  (w: any) => w.uid === newMeshFlow.uid,
                );

                if (!workflow) {
                  console.error("Created workflow not found in response");
                  return;
                }

                // Transform nodes from backend format to React Flow format
                const transformedNodes = (workflow.nodes || [])
                  .map((node: any) => {
                    const reactFlowNode = transformBackendNodeToReactFlow(
                      node,
                      {
                        onDelete: handleDeleteNode,
                        onDataChange: handleNodeDataChange,
                      },
                    );

                    // Fix position only if it's truly invalid (0,0 or backend's bad default)
                    // Backend sometimes returns positions around (-1833, -62) for new workflows
                    const isBadBackendDefault =
                      (reactFlowNode.position.x < -1500 &&
                        reactFlowNode.position.x > -2000) ||
                      (reactFlowNode.position.y < -100 &&
                        reactFlowNode.position.y > -200);

                    if (
                      (reactFlowNode.position.x === 0 &&
                        reactFlowNode.position.y === 0) ||
                      isBadBackendDefault
                    ) {
                      if (reactFlowNode.type === "startNode") {
                        reactFlowNode.position = { x: 100, y: 250 };
                      } else {
                        reactFlowNode.position = { x: 250, y: 250 };
                      }
                    }

                    return reactFlowNode;
                  })
                  .map((node: Node) => {
                    // Update configuration nodes with fresh API data instead of saved data
                    if (node.type === "authentication") {
                      const nodeConfigUid = node.data?.configData?.uid;
                      const freshConfig = configurations.find(
                        (config) => config.uid === nodeConfigUid,
                      );

                      if (freshConfig) {
                        return {
                          ...node,
                          data: {
                            ...node.data,
                            configData: freshConfig, // Use fresh API data
                            header: freshConfig.title, // Update header with current title
                            lastUpdated: Date.now(), // Force re-render
                          },
                        };
                      }
                    }
                    return node;
                  });

                // Create a map of nodes for edge transformation
                const nodesMap = new Map<string, Node>(
                  transformedNodes.map((node: Node) => [node.id, node]),
                );

                const transformedEdges = transformConnectionsToEdges(
                  workflow.connections,
                  nodesMap,
                );

                // Update active mesh flow with the actual data from backend
                const meshFlowData = {
                  uid: workflow.uid,
                  title: workflow.title,
                  description: workflow.description,
                  type_workflow: workflow.type_workflow,
                  version: workflow.version,
                  is_active: workflow.is_active || false,
                  is_export: workflow.is_export || false,
                  cron_run_time: workflow.cron_run_time,
                  dependency_workflow: workflow.dependency_workflow || [],
                  nodes: transformedNodes,
                  edges: transformedEdges,
                  connections: workflow.connections || {},
                };

                setActiveMeshFlow(meshFlowData);
                setActiveMeshFlowUid(meshFlowData.uid);

                // Load the nodes and edges to canvas
                setNodes(transformedNodes);
                setEdges(transformedEdges);

                // Clear unsaved changes flag for new workflow
                setHasUnsavedChanges(false);

                // Fit view to show all nodes properly after a brief delay
                setTimeout(() => {
                  if (reactFlowInstance) {
                    reactFlowInstance.fitView({ padding: 0.1, maxZoom: 1 });
                  }
                }, 100);
              }

              queryClient.refetchQueries(["getAllWorkflows", pluginId]);
            },
            onError: (retryError: any) => {
              // Remove from local state on retry failure
              setMeshFlows(meshFlows);
              setActiveMeshFlow(null);
              setActiveMeshFlowUid(null);
              setNodes([]);
              setEdges([]);

              const retryErrorMessage =
                retryError?.response?.data?.message ||
                retryError?.message ||
                "Failed to create workflow. Please try again.";

              toast({
                variant: "destructive",
                title: "Error Creating Workflow",
                description: retryErrorMessage,
                duration: 5000,
              });
            },
          });
          return; // Exit early, don't show error toast yet
        }

        // Remove from local state on error since save failed
        setMeshFlows(meshFlows);
        setActiveMeshFlow(null);
        setActiveMeshFlowUid(null);
        setNodes([]);
        setEdges([]);

        toast({
          variant: "destructive",
          title: "Error Creating Workflow",
          description: errorMessage,
          duration: 5000,
        });
      },
    });
  };

  // on mount , set the exportedMeshflows state with the data from local storage with the name "meshflow-data"
  React.useEffect(() => {
    const savedMeshflow = localStorage.getItem("meshflow-data");
    if (!savedMeshflow) return;
    const parsedMeshflow = JSON.parse(savedMeshflow);
    setExportedMeshflows(Array.isArray(parsedMeshflow) ? parsedMeshflow : []);
  }, []);

  // Create a stable dependency for edge label updates
  // Only changes when the actual data we care about changes (not array reference)
  const nodeDataSignature = React.useMemo(() => {
    return JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        type: n.type,
        response: n.data?.response,
        outputData: n.data?.outputData,
        node_data: n.data?.node_data,
        configData: n.data?.configData,
        sub_type: n.data?.sub_type,
      })),
    );
  }, [nodes]);

  // Update edge labels when node data changes (response/outputData)
  React.useEffect(() => {
    // Skip if we're currently loading a workflow
    if (isLoadingWorkflowRef.current) return;

    setEdges((eds: Edge[]) => {
      let hasChanges = false;

      const updatedEdges = eds.map((edge: Edge) => {
        const sourceNode = nodes.find((n) => n.id === edge.source);
        if (!sourceNode) return edge;

        let itemCount = 0;

        if (sourceNode.type === "addButtonNode") {
          // AddButton node: trace back to find the loop node
          // The chain is: LoopNode -> ... -> LastNode -> AddButton
          // We need to find the loop node to get its itemCount
          let currentNodeId = edge.source;
          let loopNode = null;
          const visited = new Set<string>();

          // Traverse backwards through incoming edges to find the loop node
          while (currentNodeId && !visited.has(currentNodeId)) {
            visited.add(currentNodeId);
            const currentNode = nodes.find((n) => n.id === currentNodeId);

            if (
              currentNode?.type === "loopOverItemsNode" ||
              currentNode?.data?.sub_type === "loop_over_items"
            ) {
              loopNode = currentNode;
              break;
            }

            // Find incoming edge
            const incomingEdge = eds.find((e) => e.target === currentNodeId);
            if (!incomingEdge) break;

            currentNodeId = incomingEdge.source;
          }

          // Get itemCount from loop node's "loop" handle (index 1 in node_data)
          if (loopNode) {
            const nodeData = loopNode.data?.node_data || [];
            if (nodeData.length > 1) {
              itemCount = nodeData[1]?.itemCount || 0;
            } else if (loopNode.data?.response) {
              const response = loopNode.data.response;
              itemCount = Array.isArray(response) ? response.length : 1;
            } else if (loopNode.data?.outputData) {
              const outputData = loopNode.data.outputData;
              itemCount = Array.isArray(outputData) ? outputData.length : 1;
            }
          }
        } else if (
          sourceNode.type === "switchNode" ||
          sourceNode.data?.sub_type === "switch_case"
        ) {
          // Switch node: get itemCount from specific handle's node_data
          const nodeData = sourceNode.data?.node_data || [];
          if (edge.sourceHandle && nodeData.length > 0) {
            // Extract handle index from sourceHandle (e.g., "output_0" -> 0)
            const handleMatch = edge.sourceHandle.match(/output_(\d+)/);
            if (handleMatch) {
              const handleIndex = parseInt(handleMatch[1], 10);
              const handleData = nodeData[handleIndex];
              itemCount = handleData?.itemCount || 0;
            }
          }
        } else if (
          sourceNode.type === "ifElseNode" ||
          sourceNode.data?.sub_type === "if_else"
        ) {
          const nodeData = sourceNode.data?.node_data || [];
          if (edge.sourceHandle && nodeData.length > 0) {
            const handleIndex = edge.sourceHandle === "false" ? 1 : 0;
            const handleData = nodeData[handleIndex];
            itemCount = handleData?.itemCount || 0;
          }
        } else if (
          sourceNode.type === "loopOverItemsNode" ||
          sourceNode.data?.sub_type === "loop_over_items"
        ) {
          // Loop node: get itemCount from specific handle's node_data
          const nodeData = sourceNode.data?.node_data || [];
          if (edge.sourceHandle && nodeData.length > 0) {
            // For loop: "done" -> index 0, "loop" -> index 1
            const handleIndex = edge.sourceHandle === "loop" ? 1 : 0;
            const handleData = nodeData[handleIndex];
            itemCount = handleData?.itemCount || 0;
          }
        } else {
          // Normal case: get itemCount from source node's data
          if (sourceNode.data?.response) {
            const response = sourceNode.data.response;
            itemCount = Array.isArray(response) ? response.length : 1;
          } else if (sourceNode.data?.outputData) {
            const outputData = sourceNode.data.outputData;
            itemCount = Array.isArray(outputData) ? outputData.length : 1;
          } else if (sourceNode.data?.configData) {
            itemCount = 1;
          }
        }

        // Only update if itemCount has actually changed
        if (edge.data?.itemCount !== itemCount) {
          hasChanges = true;
          return {
            ...edge,
            data: {
              ...edge.data,
              itemCount,
            },
          };
        }
        return edge;
      });

      // Avoid re-render if nothing changed
      return hasChanges ? updatedEdges : eds;
    });
  }, [nodeDataSignature, setEdges]);

  // Load workflows from API
  React.useEffect(() => {
    if (workflowsData?.data) {
      const workflows = Array.isArray(workflowsData.data)
        ? workflowsData.data
        : [];

      // Transform API data to match expected format
      // Preserve nodes and connections from backend for inactive workflows
      // IMPORTANT: Clean all nodes when loading from backend to remove bad data
      const transformedFlows = workflows.map((workflow: any) => {
        const workflowNodes = workflow.nodes || [];
        // Clean all nodes from backend to remove fieldsToInclude arrays and other bad data
        const cleanedNodes = workflowNodes.map((node: any) =>
          cleanBackendNode(node),
        );

        return {
          uid: workflow.uid,
          title: workflow.title,
          description: workflow.description,
          type_workflow: workflow.type_workflow,
          version: workflow.version,
          is_active: workflow.is_active || false,
          is_export: workflow.is_export || false,
          cron_run_time: workflow.cron_run_time,
          dependency_workflow: workflow.dependency_workflow || [],
          threat_lookup_search_types: workflow.threat_lookup_search_types || [],
          threatlookup_data: workflow.threatlookup_data || {},
          workflow_priority: workflow.workflow_priority || "",
          nodes: cleanedNodes, // Use cleaned nodes instead of raw nodes
          connections: workflow.connections || {}, // Preserve connections from backend
          edges: [],
        };
      });

      setMeshFlows(transformedFlows);

      // Update activeMeshFlow if it exists and matches one of the updated workflows
      // This ensures workflow metadata (like workflow_priority) stays in sync with backend
      if (activeMeshFlowUid) {
        const updatedActiveFlow = transformedFlows.find(
          (flow: any) => flow.uid === activeMeshFlowUid,
        );
        if (updatedActiveFlow && activeMeshFlow) {
          // Only update metadata fields, preserve current nodes/edges from state
          setActiveMeshFlow({
            ...activeMeshFlow,
            title: updatedActiveFlow.title,
            description: updatedActiveFlow.description,
            type_workflow: updatedActiveFlow.type_workflow,
            version: updatedActiveFlow.version,
            is_active: updatedActiveFlow.is_active,
            is_export: updatedActiveFlow.is_export,
            cron_run_time: updatedActiveFlow.cron_run_time,
            dependency_workflow: updatedActiveFlow.dependency_workflow,
            threat_lookup_search_types:
              updatedActiveFlow.threat_lookup_search_types,
            threatlookup_data: updatedActiveFlow.threatlookup_data,
            workflow_priority: updatedActiveFlow.workflow_priority,
          });
        }
      }
    }
  }, [workflowsData]);

  // Auto-save current mesh flow's nodes and edges whenever they change
  React.useEffect(() => {
    if (!activeMeshFlow || !activeMeshFlowUid) return;

    // Skip if we're currently loading a workflow
    if (isLoadingWorkflowRef.current) return;

    // Get all nodes including configuration nodes
    const workflowNodes = nodes;

    // Auto-fix edges that connect to merge nodes without targetHandle
    // Track unique sources per merge node for sequential index assignment
    const mergeNodeSources: Record<string, string[]> = {};

    const fixedEdges = edges.map((edge) => {
      // Skip if edge already has targetHandle
      if (edge.targetHandle) return edge;

      // Check if target is a merge node
      const targetNode = nodes.find((n) => n.id === edge.target);
      if (targetNode && targetNode.type === "mergeNode") {
        // Track unique sources for this merge node
        if (!mergeNodeSources[edge.target]) {
          // Get all unique sources connecting to this merge node
          const uniqueSources = [
            ...new Set(
              edges
                .filter((e) => e.target === edge.target)
                .map((e) => e.source),
            ),
          ];
          mergeNodeSources[edge.target] = uniqueSources;
        }

        // Find the index of this source in the list of unique sources
        const sourceIndex = mergeNodeSources[edge.target].indexOf(edge.source);

        if (sourceIndex >= 0) {
          // Assign targetHandle based on source index (1-based)
          return {
            ...edge,
            targetHandle: `input-${sourceIndex + 1}`,
          };
        }
      }
      return edge;
    });

    // Update edges if any were fixed
    const edgesChanged = JSON.stringify(edges) !== JSON.stringify(fixedEdges);
    if (edgesChanged) {
      setEdges(fixedEdges);
    }

    // Transform edges to connections to keep them in sync
    const connections = transformEdgesToConnections(fixedEdges, workflowNodes);

    // Filter out addButtonNode before storing (it's a UI helper only)
    const storableNodes = nodes.filter((node) => node.type !== "addButtonNode");

    // Update the mesh flow in the list with current nodes, edges, and connections
    const updatedMeshFlows = meshFlows.map((flow) =>
      flow.uid === activeMeshFlowUid
        ? { ...flow, nodes: storableNodes, edges: fixedEdges, connections }
        : flow,
    );

    // Only update if there's an actual change
    const hasChanged =
      JSON.stringify(meshFlows) !== JSON.stringify(updatedMeshFlows);
    if (hasChanged) {
      setMeshFlows(updatedMeshFlows);
      // Also update the active mesh flow state
      setActiveMeshFlow({
        ...activeMeshFlow,
        nodes: storableNodes,
        edges: fixedEdges,
        connections,
      });
    }
  }, [nodes, edges, activeMeshFlowUid]);

  // Handle making a mesh flow active
  const handleSetActiveMeshFlow = (meshFlow: any) => {
    if (!meshFlow || !meshFlow.uid) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Invalid workflow data. Please refresh the page.",
        duration: 3000,
      });
      return;
    }

    // Check for unsaved changes before switching workflows
    if (
      hasUnsavedChanges &&
      activeMeshFlow &&
      activeMeshFlow.uid !== meshFlow.uid
    ) {
      setPendingNavigation({ type: "switch", data: meshFlow });
      setShowSaveConfirmDialog(true);
      return;
    }

    // Validate pluginId before making API call
    if (!pluginId) {
      console.error("Cannot load workflow: pluginId is missing");
      toast({
        variant: "destructive",
        title: "Plugin Not Found",
        description: "Please save the plugin first before loading workflows.",
        duration: 4000,
      });
      return;
    }

    setLoadingWorkflowUid(meshFlow.uid);
    fetchWorkflowMutation.mutate({ workflowId: meshFlow.uid, pluginId });
  };

  // Handle pending navigation after save
  const handlePendingNavigation = () => {
    if (!pendingNavigation) return;

    const { type, data } = pendingNavigation;

    // Reset pending navigation
    setPendingNavigation(null);
    setShowSaveConfirmDialog(false);

    switch (type) {
      case "back":
        sessionStorage.removeItem("activeMeshFlow");
        navigate("/ai-engine", { replace: true });
        break;

      case "switch":
        if (data && data.uid) {
          setLoadingWorkflowUid(data.uid);
          fetchWorkflowMutation.mutate({ workflowId: data.uid, pluginId });
        }
        break;

      case "deselect":
        setActiveMeshFlow(null);
        setActiveMeshFlowUid(null);
        setNodes([]);
        setEdges([]);
        sessionStorage.removeItem("activeMeshFlow");
        setHasUnsavedChanges(false);
        break;
    }
  };

  // Handle save confirmation
  const handleSaveAndProceed = () => {
    // Save the workflow
    handleSaveMeshflow();
    // Navigation will happen in saveWorkflowMutation.onSuccess
  };

  // Handle cancel save confirmation
  const handleCancelSave = () => {
    setShowSaveConfirmDialog(false);
    setPendingNavigation(null);
  };

  // Handle discard changes and proceed
  const handleDiscardAndProceed = () => {
    setHasUnsavedChanges(false);
    setShowSaveConfirmDialog(false);

    // Execute pending navigation immediately
    if (pendingNavigation) {
      const { type, data } = pendingNavigation;
      setPendingNavigation(null);

      switch (type) {
        case "back":
          sessionStorage.removeItem("activeMeshFlow");
          navigate("/ai-engine", { replace: true });
          break;

        case "switch":
          if (data && data.uid) {
            setLoadingWorkflowUid(data.uid);
            fetchWorkflowMutation.mutate({ workflowId: data.uid, pluginId });
          }
          break;

        case "deselect":
          setActiveMeshFlow(null);
          setActiveMeshFlowUid(null);
          setNodes([]);
          setEdges([]);
          sessionStorage.removeItem("activeMeshFlow");
          break;

        case "backToUIPages":
          navigate("/create-node-plugin/ui?edit=1", {
            replace: true,
            state: {
              pluginId: sessionStorage.getItem("meshPluginId"),
              isEdit: true,
            },
          });
          break;
      }
    }
  };

  // Handle Back to UI Pages navigation
  const handleBackToUIPages = () => {
    // Check for unsaved changes
    if (hasUnsavedChanges && activeMeshFlow) {
      setPendingNavigation({ type: "backToUIPages" });
      setShowSaveConfirmDialog(true);
    } else {
      // No unsaved changes, navigate directly
      navigate("/create-node-plugin/ui?edit=1", {
        replace: true,
        state: {
          pluginId: sessionStorage.getItem("meshPluginId"),
          isEdit: true,
        },
      });
    }
  };

  // Edge hover handlers to highlight edges
  const onEdgeMouseEnter = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      setEdges((eds) =>
        eds.map((e) =>
          e.id === edge.id
            ? {
                ...e,
                style: {
                  ...e.style,
                  stroke: "var(--muted-foreground)",
                  strokeWidth: 2,
                },
              }
            : e,
        ),
      );
    },
    [setEdges],
  );

  // Edge hover leave handler to reset edge style
  const onEdgeMouseLeave = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      setEdges((eds) =>
        eds.map((e) =>
          e.id === edge.id
            ? {
                ...e,
                style: {
                  ...e.style,
                  stroke: undefined,
                  strokeWidth: 2,
                },
              }
            : e,
        ),
      );
    },
    [setEdges],
  );

  // Check if a node title is unique (case-insensitive)
  const isNodeTitleUnique = useCallback(
    (title: string, currentNodeId: string, allNodes: any[]) => {
      const normalizeTitle = (value: unknown): string => {
        if (typeof value === "string") return value;
        if (typeof value === "number" || typeof value === "boolean") {
          return String(value);
        }
        return "";
      };

      const safeTitle = normalizeTitle(title);
      if (!safeTitle.trim()) return true; // Empty titles are allowed (will be handled by required validation)

      const trimmedTitle = safeTitle.trim().toLowerCase();
      const duplicate = allNodes.find((node) => {
        if (node.id === currentNodeId) return false;
        const nodeTitle = normalizeTitle(
          (node.data as any)?.title || (node.data as any)?.header,
        )
          .trim()
          .toLowerCase();
        return nodeTitle !== "" && nodeTitle === trimmedTitle;
      });

      return !duplicate;
    },
    [],
  );

  // run all meshflow
  const handleRunMeshFlow = useCallback(async () => {
    // Validate pluginId first
    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin before executing the workflow.",
        duration: 4000,
      });
      return;
    }

    if (!activeMeshFlow) {
      toast({
        variant: "destructive",
        title: "No Active Mesh Flow",
        description: "Please create or select a mesh flow first.",
        duration: 3000,
      });
      return;
    }

    if (nodes.length === 0) {
      toast({
        variant: "destructive",
        title: "Nothing to Execute",
        description: "Add nodes to your meshflow before running.",
        duration: 3000,
      });
      return;
    }
    // return if there is no configuration node in the workflow

    // Only execute nodes that are part of at least one valid connection.
    // This prevents disconnected nodes from being sent in workflowData.nodes.
    const executableCandidates = nodes.filter(
      (node) => node.type !== "addButtonNode",
    );
    const executableNodeIds = new Set(
      executableCandidates.map((node) => node.id),
    );

    const connectedNodeIds = new Set<string>();
    edges.forEach((edge) => {
      if (
        executableNodeIds.has(edge.source) &&
        executableNodeIds.has(edge.target)
      ) {
        connectedNodeIds.add(edge.source);
        connectedNodeIds.add(edge.target);
      }
    });

    const workflowNodes = executableCandidates.filter((node) =>
      connectedNodeIds.has(node.id),
    );

    const configs = workflowNodes
      .filter(
        (node: any) =>
          node.type.includes("configuration") ||
          node.type.includes("authentication"),
      )
      .every((configNode: any) => configNode.data?.configData);

    if (!configs) {
      toast({
        variant: "destructive",
        title: "Missing Configuration.",
        description:
          "Configuration data is missing . Create a new configuration data or replace the existing configuration data.",
        duration: 5000,
      });
      return;
    }

    if (workflowNodes.length === 0) {
      toast({
        variant: "destructive",
        title: "No Workflow Nodes",
        description:
          "Connect workflow nodes before executing. Disconnected nodes are skipped.",
        duration: 3000,
      });
      return;
    }

    // Transform nodes to backend format and apply safety cleanup
    let transformedNodes;
    try {
      transformedNodes = workflowNodes.map((node) => {
        const transformed = transformNodeToBackend(node, pluginId);

        // Apply final safety cleanup similar to handleUpdateMeshFlow
        if (transformed.parameters) {
          // Clean up split_out nodes
          if (transformed.sub_type === "split_out") {
            if (
              Array.isArray(transformed.parameters.fieldsToInclude) ||
              !transformed.parameters.fieldsToInclude ||
              (transformed.parameters.include !== "selectedOtherFields" &&
                "fieldsToInclude" in transformed.parameters)
            ) {
              transformed.parameters.fieldsToInclude = "";
            }
          }
          // Clean up aggregate nodes
          if (transformed.sub_type === "aggregate") {
            if (transformed.parameters.include !== "specifiedFields") {
              transformed.parameters.fieldsToInclude = "";
            }
            if (transformed.parameters.include !== "allFieldsExcept") {
              transformed.parameters.fieldsToExclude = "";
            }
          }
          // Clean up Function nodes
          if (transformed.sub_type === "functional_node_linkshadow") {
            if (
              transformed.parameters.mappingThreatLookup &&
              !transformed.parameters.mappingDataList
            ) {
              transformed.parameters.mappingDataList =
                transformed.parameters.mappingThreatLookup;
            }
            delete transformed.parameters.mappingThreatLookup;
            if (!transformed.parameters.mappingDataList) {
              transformed.parameters.mappingDataList = [];
            }
          }
        }
        return transformed;
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Workflow Validation Error",
        description:
          error?.message || "One or more nodes have invalid configuration",
        duration: 5000,
      });
      return;
    }

    // Build connections using all edges
    const connections = transformEdgesToConnections(edges, workflowNodes);

    // Sort connections and nodes in topological order for execution
    const sortedConnections = sortConnectionsTopologically(connections);
    const sortedTransformedNodes = sortNodesTopologically(
      transformedNodes,
      connections,
    );

    // Build pin_data from nodes that have execution results or manual edits
    const pin_data: Record<string, any[]> = {};

    const prepareExecutionData = (data: any): any[] => {
      if (!data) return [];

      // If data is an array, we need to map each item to an INodeExecutionData object
      if (Array.isArray(data)) {
        // If already in backend format, return as is
        if (
          data.length > 0 &&
          typeof data[0] === "object" &&
          data[0] !== null &&
          "json_data" in data[0]
        ) {
          return data;
        }

        return data.map((item) => ({
          json_data:
            typeof item === "object" && item !== null && !Array.isArray(item)
              ? item
              : { data: item },
          binary: {},
          pairedItem: { item: 0 },
          error: null,
          logs: [],
        }));
      }

      // Single item
      return [
        {
          json_data:
            typeof data === "object" && data !== null && !Array.isArray(data)
              ? data
              : { data: data },
          binary: {},
          pairedItem: { item: 0 },
          error: null,
          logs: [],
        },
      ];
    };

    workflowNodes.forEach((node) => {
      // ONLY pin if it was manually edited
      if (node.data.manuallyEdited) {
        const nodeUid = node.data.uid || node.id;
        pin_data[nodeUid] = prepareExecutionData(
          node.data.response ||
            node.data.executionResult?.output_data ||
            node.data.executionResult,
        );
      }
    });

    // Build workflow data matching the backend schema
    const workflowData = {
      uid: activeMeshFlow.uid,
      title: activeMeshFlow.title,
      ...(activeMeshFlow.description && {
        description: activeMeshFlow.description,
      }),
      type_workflow: activeMeshFlow.type_workflow,
      version: activeMeshFlow.version || "1.0",
      is_active: activeMeshFlow.is_active || false,
      cron_run_time: activeMeshFlow.cron_run_time || null,
      dependency_workflow: activeMeshFlow.dependency_workflow || [],
      threat_lookup_search_types:
        activeMeshFlow.threat_lookup_search_types || [],
      threatlookup_data: activeMeshFlow.threatlookup_data || {},
      workflow_priority: activeMeshFlow.workflow_priority || "",
      nodes: sortedTransformedNodes,
      connections: sortedConnections,
      pin_data: pin_data,
      plugin_id: pluginId,
    };

    // Build execution payload containing only workflowData as expected by backend
    const executionPayload = {
      workflowData: workflowData,
    };

    // Prevent execution when any Function node is missing Action Type.
    const functionNodesWithoutAction = sortedTransformedNodes.filter(
      (node: any) => {
        if (node?.sub_type !== "functional_node_linkshadow") return false;
        const actionValue = node?.parameters?.action;
        return (
          actionValue === undefined ||
          actionValue === null ||
          String(actionValue).trim() === "" ||
          String(actionValue).trim().toLowerCase() === "none"
        );
      },
    );

    if (functionNodesWithoutAction.length > 0) {
      const nodeNames = functionNodesWithoutAction
        .map((n: any) => n?.title || "Function")
        .join(", ");

      toast({
        variant: "destructive",
        title: "Missing Action Type",
        description: `Function node(s) "${nodeNames}" must have an Action Type selected before running the workflow.`,
        duration: 5000,
      });
      return;
    }

    // Execute the workflow
    const pythonNodesWithoutResult = workflowNodes.filter(
      (node) =>
        node.type === "pythonCodeNode" &&
        node.data?.code &&
        !node.data.code.includes("result"),
    );

    if (pythonNodesWithoutResult.length > 0) {
      const nodeNames = pythonNodesWithoutResult
        .map((n) => n.data?.title || "Python Code")
        .join(", ");
      toast({
        variant: "destructive",
        title: "Missing Output Variable",
        description: `Python node(s) "${nodeNames}" must assign output to 'result'. Example: result = {'key': 'value'}`,
        duration: 5000,
      });
      return;
    }

    // Execute the workflow
    executeWorkflowMutation.mutate(executionPayload);
  }, [pluginId, activeMeshFlow, nodes, edges, toast, executeWorkflowMutation]);

  // Handle node data changes (e.g., label or conditions updates)
  const handleNodeDataChange = useCallback(
    (nodeId: string, updates: any) => {
      // Check for title uniqueness if title is being updated
      if (updates.title !== undefined) {
        let isUnique = false;

        setNodes((currentNodes) => {
          isUnique = isNodeTitleUnique(updates.title, nodeId, currentNodes);

          if (!isUnique) {
            // Return nodes unchanged - reject the update (no toast, inline error will show)
            return currentNodes;
          }

          // Title is unique, proceed with update
          return currentNodes.map((node) => {
            if (node.id === nodeId) {
              // Check if data actually changed to prevent infinite loops
              const hasChanges = Object.keys(updates).some(
                (key) =>
                  JSON.stringify(node.data[key]) !==
                  JSON.stringify(updates[key]),
              );

              if (!hasChanges) {
                return node; // No changes, return same reference
              }

              return { ...node, data: { ...node.data, ...updates } };
            }
            return node;
          });
        });

        // If title validation failed, stop here (don't process edges)
        // Inline error in the node will show the duplicate message
        if (!isUnique) {
          return;
        }
      }

      // No title update, proceed normally
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            // Check if data actually changed to prevent infinite loops
            const hasChanges = Object.keys(updates).some(
              (key) =>
                JSON.stringify(node.data[key]) !== JSON.stringify(updates[key]),
            );

            if (!hasChanges) {
              return node; // No changes, return same reference
            }

            // Mark as unsaved when node data changes (but not during workflow loading)
            if (!isLoadingWorkflowRef.current) {
              setHasUnsavedChanges(true);
            }

            // Immediately trigger workflow execution if this was a manual edit to response
            if (updates.manuallyEdited && activeMeshFlow) {
              // We need to wait for the state update to propagate or pass the updated nodes directly
              setTimeout(() => {
                handleRunMeshFlow();
              }, 300); // Increased timeout to ensure state is settled
            }

            return { ...node, data: { ...node.data, ...updates } };
          }
          return node;
        }),
      );

      // Clean up orphaned edges when node_data changes (e.g., when switch rules are deleted)
      // AND update edge itemCounts based on node_data
      if (updates.node_data !== undefined) {
        const newNodeData = updates.node_data || [];

        setEdges((eds) => {
          return eds
            .filter((edge) => {
              // Only check edges from this node
              if (edge.source !== nodeId) return true;

              // If no sourceHandle, keep the edge
              if (!edge.sourceHandle) return true;

              // Check if the sourceHandle still exists in node_data
              // For switch nodes: sourceHandle is like "output_0", "output_1", etc.
              const handleMatch = edge.sourceHandle.match(/output_(\d+)/);
              if (handleMatch) {
                const handleIndex = parseInt(handleMatch[1], 10);
                // Remove edge if handle index is >= node_data length (handle no longer exists)
                if (handleIndex >= newNodeData.length) {
                  return false;
                }
              }

              return true;
            })
            .map((edge) => {
              // Update itemCount for edges from this node based on node_data
              if (edge.source === nodeId && newNodeData.length > 0) {
                // Find the matching node_data entry for this edge's sourceHandle
                let itemCount = 0;

                if (edge.sourceHandle) {
                  // For IfElse nodes: sourceHandle is "true" or "false"
                  const ifElseMatch = newNodeData.find(
                    (item: any) => item.id === edge.sourceHandle,
                  );

                  if (ifElseMatch) {
                    itemCount = ifElseMatch.itemCount || 0;
                  } else {
                    // For Switch nodes: sourceHandle is "output_0", "output_1", etc.
                    const handleMatch = edge.sourceHandle.match(/output_(\d+)/);
                    if (handleMatch) {
                      const handleIndex = parseInt(handleMatch[1], 10);
                      if (
                        handleIndex >= 0 &&
                        handleIndex < newNodeData.length
                      ) {
                        itemCount = newNodeData[handleIndex].itemCount || 0;
                      }
                    }
                  }
                }

                // Return updated edge with new itemCount
                return {
                  ...edge,
                  data: {
                    ...edge.data,
                    itemCount,
                  },
                };
              }

              return edge;
            });
        });
      }
    },
    [setNodes, setEdges, isNodeTitleUnique, handleRunMeshFlow],
  );

  // Handle deletion of exported meshflow
  const handleDeleteMeshflow = (index: number) => {
    setShowDeleteNodeDialog(true);
    setNodeToDelete(index.toString());
  };

  // Confirm deletion of exported meshflow
  const onDeleteMeshflowConfirm = () => {
    if (nodeToDelete === null) return;
    const index = parseInt(nodeToDelete, 10);

    const updated = exportedMeshflows.filter((_, i) => i !== index);

    setExportedMeshflows(updated);
    localStorage.setItem("meshflow-data", JSON.stringify(updated));

    setShowDeleteNodeDialog(false);
    setNodeToDelete(null);
  };

  // Handle refresh/execute of exported meshflow node
  const handleRefreshExportedNode = async (flowId: string, nodeId: string) => {
    // Look up the workflow from localStorage using flowId (workflow uid)
    const storedMeshflows = localStorage.getItem("meshflow-data");
    let flowData = null;

    if (storedMeshflows) {
      try {
        const meshflows = JSON.parse(storedMeshflows);
        const foundFlow = meshflows.find(
          (m: any) => m.flowData?.uid === flowId,
        );
        if (foundFlow) {
          flowData = foundFlow.flowData;
        }
      } catch (error) {
        console.error("Error parsing meshflow data:", error);
      }
    }

    if (!flowData) {
      toast({
        variant: "destructive",
        title: "Flow Not Found",
        description: "Could not find the exported workflow data.",
        duration: 3000,
      });
      return;
    }

    // Mark node as running
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                executionStatus: "running",
              },
            }
          : n,
      ),
    );

    try {
      // Transform the exported flow's nodes and edges for execution
      const executableNodes =
        flowData.nodes?.filter((n: any) => n.type !== "addButtonNode") || [];

      const transformedNodes = executableNodes.map((flowNode: any) =>
        transformNodeToBackend(flowNode, pluginId),
      );

      const connections =
        flowData.connections ||
        transformEdgesToConnections(flowData.edges || [], executableNodes);

      // Sort for execution
      const sortedConnections = sortConnectionsTopologically(connections);
      const sortedNodes = sortNodesTopologically(transformedNodes, connections);

      // Build execution payload
      const executionPayload = {
        workflowData: {
          uid: flowData.uid,
          title: flowData.title,
          type_workflow: flowData.type_workflow || "action",
          version: flowData.version || "1.0",
          is_active: true,
          nodes: sortedNodes,
          connections: sortedConnections,
        },
        plugin_id: pluginId,
      };

      // Execute the workflow
      const response = await executeWorkflow(executionPayload);

      // Get the final result from the last node's json_data
      const results = response?.results?.[0];
      const nodeResults = results?.node_results || [];

      // Get the last node's output_data[0].json_data as the final output
      let finalJsonData = null;
      if (nodeResults.length > 0) {
        const lastNode = nodeResults[nodeResults.length - 1];
        if (lastNode?.output_data && lastNode.output_data.length > 0) {
          finalJsonData = lastNode.output_data[0].json_data || null;
        }
      }

      // Update node with the execution result
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: {
                  ...n.data,
                  executionStatus: "success",
                  response: finalJsonData,
                  outputData: finalJsonData,
                  executionResult: results,
                  // Update configData to show the final result as key-value pairs
                  configData: {
                    ...n.data.configData,
                    ...finalJsonData,
                  },
                },
              }
            : n,
        ),
      );

      toast({
        variant: "success",
        title: "Flow Executed",
        description: `"${flowData.title}" executed successfully.`,
        duration: 3000,
      });
    } catch (error: any) {
      // Update node with error status
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: {
                  ...n.data,
                  executionStatus: "error",
                  error:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Execution failed",
                },
              }
            : n,
        ),
      );

      toast({
        variant: "destructive",
        title: "Execution Failed",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to execute the exported flow.",
        duration: 5000,
      });
    }
  };

  const handleDeleteNode = (nodeId: string) => {
    // Mark as unsaved when node is deleted
    setHasUnsavedChanges(true);

    setNodes((nds) => {
      // Find the node being deleted from current state
      const nodeToDelete = nds.find((n) => n.id === nodeId);
      const nodesToRemove = [nodeId];

      // If deleting a Loop Over Items node, also delete its AddButton node
      if (nodeToDelete?.type === "loopOverItemsNode") {
        const addButtonNode = nds.find((n) => {
          const isAddButton = n.type === "addButtonNode";
          const hasMatchingLoopId = n.data?.loopNodeId === nodeId;
          return isAddButton && hasMatchingLoopId;
        });

        if (addButtonNode) {
          nodesToRemove.push(addButtonNode.id);
        }
      }

      // If deleting an AddButton node, just remove it (no cascade)
      // This allows manual deletion of orphaned AddButton nodes

      return nds.filter((n) => !nodesToRemove.includes(n.id));
    });

    setEdges((eds) =>
      eds.filter((e) => {
        // Re-check nodes to get the list of removed IDs including AddButton
        const currentNodes = nodes;
        const nodeToDelete = currentNodes.find((n) => n.id === nodeId);
        const nodesToRemove = [nodeId];

        if (nodeToDelete?.type === "loopOverItemsNode") {
          const addButtonNode = currentNodes.find(
            (n) => n.type === "addButtonNode" && n.data?.loopNodeId === nodeId,
          );
          if (addButtonNode) {
            nodesToRemove.push(addButtonNode.id);
          }
        }

        return (
          !nodesToRemove.includes(e.source) && !nodesToRemove.includes(e.target)
        );
      }),
    );
  };

  // Handler for node selection from search dialog
  const handleNodeSearchSelect = useCallback(
    (nodeType: string, nodeTemplate: any) => {
      if (!reactFlowInstance) {
        toast({
          variant: "destructive",
          title: "Canvas Not Ready",
          description: "Please wait for the canvas to load.",
          duration: 3000,
        });
        return;
      }

      // Check if there's an active mesh flow before allowing node creation
      if (!activeMeshFlow) {
        toast({
          variant: "destructive",
          title: "No Active Mesh Flow",
          description:
            "Please create a new mesh flow or select an existing one.",
          duration: 3000,
        });
        return;
      }

      // Check if workflow is threatlookup and prevent Save Logs/Save to Datalist nodes
      const workflowType = activeMeshFlow.type_workflow;
      const isThreatLookup =
        workflowType === "threatlookup" || workflowType === "threat_lookup";
      const isRestrictedNode =
        nodeType === "savelogs" || nodeType === "savedatalist";

      if (isThreatLookup && isRestrictedNode) {
        const nodeName =
          nodeType === "savelogs" ? "Save Logs" : "Save to Datalist";
        toast({
          variant: "destructive",
          title: "Node Not Allowed",
          description: `${nodeName} nodes are not allowed in Threat Lookup workflows.`,
          duration: 4000,
        });
        return;
      }

      // Get the center of the current viewport
      const bounds = reactFlowWrapper.current?.getBoundingClientRect();

      // Calculate center position in flow coordinates
      const centerX = (bounds?.width ?? 800) / 2;
      const centerY = (bounds?.height ?? 600) / 2;

      const flowPosition = reactFlowInstance.project({
        x: centerX,
        y: centerY,
      });

      const config = nodeConfigs[nodeType] || { extraData: {} };
      const nodeId = getId(nodeTemplate.header);

      const newNode = createNode({
        id: nodeId,
        type: nodeType,
        position: flowPosition,
        header: nodeTemplate.header,
        borderColor: nodeTemplate.borderColor,
        shadowColor: nodeTemplate.shadowColor,
        iconStyle: nodeTemplate.iconStyle || "text-muted-foreground",
        onDelete: handleDeleteNode,
        onDataChange: handleNodeDataChange,
        ...config.extraData,
      });

      setNodes((nds) => [...nds, newNode]);
      setHasUnsavedChanges(true); // Mark as unsaved when node is added

      toast({
        variant: "success",
        title: `${nodeTemplate.header} Added`,
        description: `${nodeTemplate.header} Node has been added to the workflow.`,
        duration: 2000,
      });
    },
    [reactFlowInstance, activeMeshFlow, setNodes],
  );

  // Handler for adding nodes to loop
  const handleAddNodeToLoop = useCallback(
    (nodeType: string, addButtonNodeId: string, loopNodeId: string) => {
      // Get node template
      const allNodeTemplates = [
        ...logicNodes,
        ...dataTransmission,
        ...customNodes,
      ];
      const nodeTemplate = allNodeTemplates.find((n) => n.type === nodeType);
      if (!nodeTemplate) {
        return;
      }

      // Check if workflow is threatlookup and prevent Save Logs/Save to Datalist nodes
      if (activeMeshFlow) {
        const workflowType = activeMeshFlow.type_workflow;
        const isThreatLookup =
          workflowType === "threatlookup" || workflowType === "threat_lookup";
        const isRestrictedNode =
          nodeType === "savelogs" || nodeType === "savedatalist";

        if (isThreatLookup && isRestrictedNode) {
          const nodeName =
            nodeType === "savelogs" ? "Save Logs" : "Save to Datalist";
          toast({
            variant: "destructive",
            title: "Node Not Allowed",
            description: `${nodeName} nodes are not allowed in Threat Lookup workflows.`,
            duration: 4000,
          });
          return;
        }
      }

      // Get config for the node type
      const config = nodeConfigs[nodeType] || { extraData: {} };

      // Create the new node
      const nodeId = getId(nodeTemplate.header);

      // Get positions from current nodes state
      setNodes((currentNodes) => {
        const loopNode = currentNodes.find((n) => n.id === loopNodeId);
        const addButtonNode = currentNodes.find(
          (n) => n.id === addButtonNodeId,
        );

        if (!loopNode || !addButtonNode) {
          return currentNodes;
        }

        // Find all nodes in the loop chain (nodes between loop and addButton)
        const findNodesInLoopChain = () => {
          const chainNodes: typeof currentNodes = [];
          const visited = new Set<string>();

          // Start from edges coming out of loop node's "loop" handle
          const queue: string[] = [];

          // Get all edges to find the chain
          edges.forEach((edge) => {
            if (edge.source === loopNodeId && edge.sourceHandle === "loop") {
              if (edge.target !== addButtonNodeId) {
                queue.push(edge.target);
              }
            }
          });

          // Traverse the chain until we reach addButton
          while (queue.length > 0) {
            const currentId = queue.shift()!;
            if (visited.has(currentId) || currentId === addButtonNodeId) {
              continue;
            }
            visited.add(currentId);

            const node = currentNodes.find((n) => n.id === currentId);
            if (node) {
              chainNodes.push(node);

              // Find next node in chain
              edges.forEach((edge) => {
                if (edge.source === currentId && edge.target !== loopNodeId) {
                  queue.push(edge.target);
                }
              });
            }
          }

          return chainNodes;
        };

        const chainNodes = findNodesInLoopChain();

        // Calculate position for new node
        // Position it to the right of the last node in the chain (or loop node if chain is empty)
        const lastNodeInChain =
          chainNodes.length > 0 ? chainNodes[chainNodes.length - 1] : null;

        const baseNode = lastNodeInChain || loopNode;
        const flowPosition = {
          x: baseNode.position.x + 300, // Position to the right
          y: loopNode.position.y, // Keep same vertical level as loop node
        };

        const newNode = createNode({
          id: nodeId,
          type: nodeType,
          position: flowPosition,
          header: nodeTemplate.header,
          borderColor: nodeTemplate.borderColor,
          shadowColor: nodeTemplate.shadowColor,
          iconStyle: nodeTemplate.iconStyle,
          extraData: {
            ...config.extraData,
            uid: nodeId,
          },
          onDelete: handleDeleteNode,
          onDataChange: handleNodeDataChange,
        });

        // Calculate AddButton position - move it down and to the right to create loop visual
        const nodeCount = chainNodes.length + 1; // +1 for the new node
        const addButtonPosition = {
          x: flowPosition.x + 200, // Further to the right
          y: loopNode.position.y + 150 + nodeCount * 20, // Move down progressively
        };

        // Add the new node and reposition AddButton
        const updatedNodes = [...currentNodes, newNode].map((n) => {
          if (n.id === addButtonNodeId) {
            // Move AddButton down and to the right to form a visual loop
            return {
              ...n,
              position: addButtonPosition,
            };
          }
          return n;
        });

        return updatedNodes;
      });

      // Mark as unsaved when node is added to loop
      setHasUnsavedChanges(true);

      // Re-wire the edges:
      // Find the node that currently connects to AddButton (this is the last node in the chain)
      // Insert the new node between that node and AddButton
      setTimeout(() => {
        setEdges((currentEdges) => {
          // Find the edge that currently goes into AddButton from the loop chain
          const edgeToAddButton = currentEdges.find(
            (e) => e.target === addButtonNodeId && e.targetHandle === "input",
          );

          if (!edgeToAddButton) {
            // No edge to AddButton found, this shouldn't happen but handle gracefully
            return currentEdges;
          }

          const sourceNodeId = edgeToAddButton.source;

          // Get itemCount from the existing edge
          const existingItemCount = edgeToAddButton.data?.itemCount || 0;

          // Remove the edge from the previous node to AddButton
          const filteredEdges = currentEdges.filter(
            (e) => e.id !== edgeToAddButton.id,
          );

          // Create two new edges:
          // 1. From the previous node (or loop node) to the new node
          const edge1: Edge = {
            id: `edge-${sourceNodeId}-${nodeId}-${Date.now()}`,
            source: sourceNodeId,
            target: nodeId,
            sourceHandle: edgeToAddButton.sourceHandle, // Preserve the source handle (e.g., "loop")
            targetHandle: undefined, // default input handle
            type: "loop",
            animated: true,
            data: { itemCount: existingItemCount, isLoopBack: false },
            style: {
              strokeWidth: 2,
              strokeDasharray: "5 3",
            },
          };

          // 2. From the new node to AddButton
          const edge2: Edge = {
            id: `edge-${nodeId}-${addButtonNodeId}-${Date.now()}`,
            source: nodeId,
            target: addButtonNodeId,
            sourceHandle: undefined, // default output handle
            targetHandle: "input",
            type: "loop",
            animated: true,
            data: { itemCount: existingItemCount, isLoopBack: false },
            style: {
              strokeWidth: 2,
              strokeDasharray: "5 3",
            },
          };

          return [...filteredEdges, edge1, edge2];
        });
      }, 100);
    },
    [setNodes, setEdges, activeMeshFlow],
  );

  // edit saved meshflow
  const handleEditMeshflow = (meshflow: any) => {
    setEditingMeshFlow(meshflow);
    setShowCreateMeshFlowDialog(true);
  };

  const handleUpdateMeshFlow = (updatedData: any) => {
    if (!editingMeshFlow) {
      return;
    }

    // Update the meshflow metadata while preserving nodes and edges
    const updatedFlows = meshFlows.map((flow) =>
      flow.uid === editingMeshFlow.uid
        ? {
            ...flow,
            title: updatedData.title,
            description: updatedData.description,
            type_workflow: updatedData.type_workflow,
            version: updatedData.version,
            is_active: updatedData.is_active,
            is_export: updatedData.is_export || false,
            cron_run_time: updatedData.cron_run_time,
            dependency_workflow: updatedData.dependency_workflow,
            threat_lookup_search_types:
              updatedData.threat_lookup_search_types || [],
            threatlookup_data: updatedData.threatlookup_data || {},
            workflow_priority: updatedData.workflow_priority || "",
          }
        : flow,
    );

    setMeshFlows(updatedFlows);

    // update active flow if needed
    if (activeMeshFlowUid === editingMeshFlow.uid) {
      const updatedActiveMeshFlow = {
        ...editingMeshFlow,
        title: updatedData.title,
        description: updatedData.description,
        type_workflow: updatedData.type_workflow,
        version: updatedData.version,
        is_active: updatedData.is_active,
        is_export: updatedData.is_export || false,
        cron_run_time: updatedData.cron_run_time,
        dependency_workflow: updatedData.dependency_workflow,
        threat_lookup_search_types:
          updatedData.threat_lookup_search_types || [],
        threatlookup_data: updatedData.threatlookup_data || {},
        workflow_priority: updatedData.workflow_priority || "",
      };
      setActiveMeshFlow(updatedActiveMeshFlow);
    }

    // Save all workflows to backend API
    const allWorkflowsData = updatedFlows.map((flow) => {
      if (flow.uid === activeMeshFlowUid) {
        // This is the active workflow, use current canvas data including config nodes
        // BUT exclude addButtonNode - it's a UI helper only, not a real workflow node
        // Keep configuration nodes even if deleted from UI Pages (configData will be null)
        const workflowNodes = nodes.filter((node) => {
          // Filter out addButtonNode only
          if (node.type === "addButtonNode") return false;

          // Keep all other nodes, including configuration nodes even if deleted
          return true;
        });

        // Build connections once
        const workflowConnections = transformEdgesToConnections(
          edges,
          workflowNodes,
        );
        const sortedConnections =
          sortConnectionsTopologically(workflowConnections);

        return {
          uid: flow.uid,
          title: flow.title,
          ...(flow.description && { description: flow.description }),
          type_workflow: flow.type_workflow,
          version: flow.version,
          is_active: flow.is_active || false,
          is_export: flow.is_export || false,
          cron_run_time:
            flow.cron_run_time ||
            (["save_log", "save_datalist", "itdr", "dspm"].includes(
              flow.type_workflow,
            )
              ? "5"
              : null),
          dependency_workflow: flow.dependency_workflow || [],
          threat_lookup_search_types: flow.threat_lookup_search_types || [],
          threatlookup_data: flow.threatlookup_data || {},
          workflow_priority: flow.workflow_priority || "",
          nodes: sortNodesTopologically(
            workflowNodes.map((node) => transformNodeToBackend(node, pluginId)),
            workflowConnections,
          ),
          connections: sortedConnections,
        };
      } else {
        // This is an inactive workflow, use stored data from API
        const inactiveNodes = Array.isArray(flow.nodes) ? flow.nodes : [];

        // Check if nodes are in React Flow format (have 'id' and 'data' instead of 'uid' and 'parameters')
        if (
          inactiveNodes.length > 0 &&
          inactiveNodes[0].id &&
          inactiveNodes[0].data
        ) {
          // Filter out addButtonNode from inactive nodes
          const filteredInactiveNodes = inactiveNodes.filter(
            (node: any) => node.type !== "addButtonNode",
          );

          // Nodes are in React Flow format, need to transform them to backend format
          const transformedInactiveNodes = filteredInactiveNodes.map(
            transformNodeToBackend,
          );

          // Transform edges to connections if edges exist
          // Use edges if available, otherwise preserve existing connections from flow
          const inactiveEdges = flow.edges || [];
          const inactiveConnections =
            inactiveEdges.length > 0
              ? transformEdgesToConnections(
                  inactiveEdges,
                  filteredInactiveNodes,
                )
              : flow.connections || {};

          // Sort connections and nodes in topological order
          const sortedInactiveConnections =
            sortConnectionsTopologically(inactiveConnections);
          const sortedInactiveNodes = sortNodesTopologically(
            transformedInactiveNodes,
            inactiveConnections,
          );

          return {
            uid: flow.uid,
            title: flow.title,
            ...(flow.description && { description: flow.description }),
            type_workflow: flow.type_workflow,
            version: flow.version,
            is_active: flow.is_active || false,
            cron_run_time:
              flow.cron_run_time ||
              (["save_log", "save_datalist", "itdr", "dspm"].includes(
                flow.type_workflow,
              )
                ? "5"
                : null),
            dependency_workflow: flow.dependency_workflow || [],
            threat_lookup_search_types: flow.threat_lookup_search_types || [],
            threatlookup_data: flow.threatlookup_data || {},
            workflow_priority: flow.workflow_priority || "",
            nodes: sortedInactiveNodes,
            connections: sortedInactiveConnections,
          };
        } else {
          // Nodes are already in backend format
          // Always preserve existing connections from flow to avoid losing edges
          const inactiveConnections = flow.connections || {};

          // Clean up nodes to remove problematic fields (like fieldsToInclude arrays)
          const cleanedNodes = inactiveNodes.map(cleanBackendNode);

          return {
            uid: flow.uid,
            title: flow.title,
            ...(flow.description && { description: flow.description }),
            type_workflow: flow.type_workflow,
            version: flow.version,
            is_active: flow.is_active || false,
            cron_run_time:
              flow.cron_run_time ||
              (["save_log", "save_datalist", "itdr", "dspm"].includes(
                flow.type_workflow,
              )
                ? "5"
                : null),
            dependency_workflow: flow.dependency_workflow || [],
            threat_lookup_search_types: flow.threat_lookup_search_types || [],
            threatlookup_data: flow.threatlookup_data || {},
            workflow_priority: flow.workflow_priority || "",
            nodes: cleanedNodes,
            connections: inactiveConnections,
          };
        }
      }
    });

    // Final cleanup: ensure all nodes are clean before sending to backend
    // This catches any backend-formatted nodes that might have slipped through
    const cleanedWorkflowsData = allWorkflowsData.map((workflow) => ({
      ...workflow,
      nodes: workflow.nodes.map((node: any) => {
        // Always clean nodes with parameters (backend format)
        if (node.parameters) {
          const cleaned = cleanBackendNode(node);
          // Double-check: ensure fieldsToInclude is completely removed for split_out with wrong mode
          if (
            cleaned.sub_type === "split_out" &&
            cleaned.parameters &&
            cleaned.parameters.include !== "selectedOtherFields" &&
            "fieldsToInclude" in cleaned.parameters
          ) {
            delete cleaned.parameters.fieldsToInclude;
          }
          return cleaned;
        }
        // Otherwise return as-is (already transformed)
        return node;
      }),
    }));

    // ABSOLUTE FINAL SAFETY CHECK - inline cleanup right before sending
    // This ensures fieldsToInclude arrays are converted to empty strings (backend expects field to exist)
    const finalCleanedData = JSON.parse(JSON.stringify(cleanedWorkflowsData));
    finalCleanedData.forEach((workflow: any) => {
      workflow.nodes?.forEach((node: any) => {
        if (node.parameters) {
          // Clean up split_out nodes - set to empty string instead of delete
          if (node.sub_type === "split_out") {
            if (Array.isArray(node.parameters.fieldsToInclude)) {
              node.parameters.fieldsToInclude = "";
            } else if (
              node.parameters.include !== "selectedOtherFields" &&
              "fieldsToInclude" in node.parameters
            ) {
              node.parameters.fieldsToInclude = "";
            } else if (!("fieldsToInclude" in node.parameters)) {
              // Always add the field if it doesn't exist
              node.parameters.fieldsToInclude = "";
            }
          }
          // Clean up aggregate nodes - set to empty string instead of delete
          if (node.sub_type === "aggregate") {
            if (node.parameters.include !== "specifiedFields") {
              node.parameters.fieldsToInclude = "";
            } else if (!("fieldsToInclude" in node.parameters)) {
              node.parameters.fieldsToInclude = "";
            }
            if (node.parameters.include !== "allFieldsExcept") {
              node.parameters.fieldsToExclude = "";
            } else if (!("fieldsToExclude" in node.parameters)) {
              node.parameters.fieldsToExclude = "";
            }
          }
          // Clean up Function nodes - migrate from legacy mappingThreatLookup to mappingDataList
          if (node.sub_type === "functional_node_linkshadow") {
            // If using legacy field name, migrate to new field name
            if (
              node.parameters.mappingThreatLookup &&
              !node.parameters.mappingDataList
            ) {
              node.parameters.mappingDataList =
                node.parameters.mappingThreatLookup;
            }
            // Remove legacy field
            delete node.parameters.mappingThreatLookup;
            // Ensure mappingDataList exists (even if empty array for non-threat-lookup actions)
            if (!node.parameters.mappingDataList) {
              node.parameters.mappingDataList = [];
            }
          }
        }
      });
    });

    const workflowPayload = {
      workflowData: finalCleanedData,
      is_edit: true,
    };

    saveWorkflowMutation.mutate(workflowPayload, {
      onSuccess: () => {
        toast({
          variant: "success",
          title: "Mesh Flow Updated!",
          description: `"${updatedData.title}" has been updated successfully.`,
          duration: 3000,
        });

        // Close the dialog on success
        setShowCreateMeshFlowDialog(false);

        // Clear editing state on success
        setEditingMeshFlow(null);

        queryClient.refetchQueries(["getAllWorkflows", pluginId]);
      },
      onError: (error: any) => {
        // Keep dialog open so user can correct the error
        toast({
          variant: "destructive",
          title: "Error Updating Workflow",
          description:
            error?.message || "Failed to update workflow. Please try again.",
          duration: 3000,
        });
      },
    });
  };

  // Handle deletion of mesh flow
  const handleDeleteMeshFlow = (uid: string) => {
    setShowDeleteMeshFlowDialog(true);
    setMeshFlowToDelete(uid);
  };

  // Confirm deletion of mesh flow
  const onDeleteMeshFlowConfirm = () => {
    if (meshFlowToDelete === null) return;

    // Use the mutation to delete the workflow via API
    deleteWorkflowMutation.mutate({
      workflowId: meshFlowToDelete,
      pluginId,
    });

    setShowDeleteMeshFlowDialog(false);
    setMeshFlowToDelete(null);
  };

  return (
    <div className="flex gap-3 w-[97%] !h-[90vh] m-5 shadow-lg rounded-xl p-2">
      <PageLoader
        show={
          deleteWorkflowMutation.isLoading || saveWorkflowMutation.isLoading
        }
      />

      <Confirmation
        text="Are you sure you want to delete this meshflow?"
        show={showDeleteNodeDialog}
        confirmAction={onDeleteMeshflowConfirm}
        confirmText="Confirm"
        rejectText="Cancel"
        rejectAction={() => setShowDeleteNodeDialog(false)}
        closeDialog={() => setShowDeleteNodeDialog(false)}
      />

      <Confirmation
        text="Are you sure you want to delete this mesh flow? "
        show={showDeleteMeshFlowDialog}
        confirmAction={onDeleteMeshFlowConfirm}
        confirmText={
          deleteWorkflowMutation.isLoading ? "Deleting..." : "Delete"
        }
        rejectText="Cancel"
        rejectAction={() =>
          !deleteWorkflowMutation.isLoading &&
          setShowDeleteMeshFlowDialog(false)
        }
        closeDialog={() =>
          !deleteWorkflowMutation.isLoading &&
          setShowDeleteMeshFlowDialog(false)
        }
      />

      {/* Save Changes Confirmation Dialog */}
      {showSaveConfirmDialog && (
        <Confirmation
          text="You have unsaved changes. Do you want to save before proceeding?"
          show={showSaveConfirmDialog}
          confirmAction={handleSaveAndProceed}
          confirmText="Save and Continue"
          rejectText="Discard Changes"
          rejectAction={handleDiscardAndProceed}
          closeDialog={handleCancelSave}
        />
      )}

      {/* _________ Sidebar _______*/}
      <MeshflowSidebar
        configState={{
          configurations,
          isLoadingConfig,
        }}
        workflowState={{
          meshFlows,
          activeMeshFlowUid,
          isLoadingWorkflows,
          loadingWorkflowUid,
          exportedMeshflows,
          meshFlowToDelete,
        }}
        loadingState={{
          isFetchingWorkflow: fetchWorkflowMutation.isLoading,
          isDeletingWorkflow: deleteWorkflowMutation.isLoading,
        }}
        handlers={{
          onDragStart,
          onCreateMeshFlowClick: () => setShowCreateMeshFlowDialog(true),
          onMeshFlowSelect: handleSetActiveMeshFlow,
          onMeshFlowEdit: handleEditMeshflow,
          onMeshFlowDelete: handleDeleteMeshFlow,
          onExportedMeshflowDelete: handleDeleteMeshflow,
          onBackToUIPages: handleBackToUIPages,
          onDeselect: () => {
            // Check for unsaved changes before deselecting
            if (hasUnsavedChanges && activeMeshFlow) {
              setPendingNavigation({ type: "deselect" });
              setShowSaveConfirmDialog(true);
            } else {
              setActiveMeshFlow(null);
              setActiveMeshFlowUid(null);
              setNodes([]);
              setEdges([]);
              sessionStorage.removeItem("activeMeshFlow");
              setHasUnsavedChanges(false);
            }
          },
        }}
      />

      {/* ______ React Flow Canvas ________*/}
      <div
        ref={reactFlowWrapper}
        className="flex-1 bg-background rounded-lg relative border"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={stableNodeTypes}
          edgeTypes={stableEdgeTypes}
          fitView
          fitViewOptions={{ padding: 0.1, maxZoom: 1 }}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={stableDefaultEdgeOptions}
          onEdgeContextMenu={onEdgeContextMenu}
          onEdgeMouseEnter={onEdgeMouseEnter}
          onEdgeMouseLeave={onEdgeMouseLeave}
        >
          <Background gap={20} size={1} />
        </ReactFlow>

        {/* Empty state message when no mesh flow is selected */}
        {!activeMeshFlow && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex flex-col p-8 rounded-2xl border-2 border-dashed border-lightRed bg-background/80 backdrop-blur-sm">
              <FallBackUi
                title="No Active Mesh Flow"
                description="Select an existing flow from the sidebar or create a new one to get started."
              />
              <button
                onClick={() => setShowCreateMeshFlowDialog(true)}
                className="flex items-center justify-center gap-2 pointer-events-auto px-4 py-2 bg-primary text-background rounded-lg hover:scale-105 transition-transform text-sm font-medium w-fit self-center mt-2"
              >
                <Plus size={18} /> Create New Mesh Flow
              </button>
            </div>
          </div>
        )}

        {/* Search button moved to top right - will be positioned in the top-right section */}

        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          {activeMeshFlow && (
            <div className="flex items-center gap-3">
              {/* Button for meshflow execution */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRunMeshFlow();
                }}
                disabled={isExecutingWorkflow || saveWorkflowMutation.isLoading}
                className={`
                  group flex items-center gap-3
                  h-11 px-4
                  rounded-xl
                  border border-lightGray
                  bg-primary backdrop-blur-xl
                  shadow-md
                  transition-all duration-200
                  hover:border-primary
                  active:scale-[0.98]
                  ${
                    isExecutingWorkflow || saveWorkflowMutation.isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }
                `}
                title="Execute Meshflow (Ctrl+Shift+E)"
              >
                {!isExecutingWorkflow ? (
                  <Play
                    size={16}
                    className="text-background transition-transform group-hover:scale-110"
                  />
                ) : (
                  <Loader size={16} className="text-background animate-spin" />
                )}
                <span className="text-sm font-medium text-background tracking-wide">
                  {isExecutingWorkflow
                    ? "Executing Meshflow"
                    : "Execute Meshflow"}
                </span>
              </button>
            </div>
          )}
        </div>

        {/* save and navigate to ai-engine button */}
        <div className="absolute left-4 top-4 z-20">
          {activeMeshFlow && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSaveAndGoToAiEngine();
              }}
              disabled={saveWorkflowMutation.isLoading || isExecutingWorkflow}
              className={`
                  group flex items-center justify-center gap-3
                  h-11 w-11
                  rounded-xl
                  border border-lightGray
                  bg-primary backdrop-blur-xl
                  shadow-md
                  transition-all duration-300 ease-in-out
                  hover:w-[240px] hover:justify-start hover:px-4
                  hover:bg-primary
                  active:scale-[0.98]
                  overflow-hidden
                  ${
                    saveWorkflowMutation.isLoading || isExecutingWorkflow
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }
                `}
              title="Save and go to AI Engine (Ctrl+Shift+S)"
            >
              <Save
                size={16}
                className="text-background transition-transform group-hover:scale-110 flex-shrink-0"
              />
              <span className="hidden text-background group-hover:inline-block text-sm font-medium tracking-wide whitespace-nowrap animate-in fade-in duration-300">
                Save & Finish
              </span>
              <div className="hidden group-hover:flex items-center gap-1 px-2 py-1 rounded-lg bg-muted/60 border border-border text-[11px] text-background font-medium whitespace-nowrap animate-in fade-in duration-300">
                <kbd>Ctrl</kbd>
                <span>+</span>
                <kbd>Shift</kbd>
                <span>+</span>
                <kbd>S</kbd>
              </div>
            </button>
          )}
        </div>

        {/* Search and Save buttons - top right */}
        <div className="absolute top-4 right-4 z-20">
          {activeMeshFlow && (
            <div className="flex items-center gap-3">
              {/* Search Node Button - Icon only, expands on hover */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowNodeSearchDialog(true);
                }}
                disabled={saveWorkflowMutation.isLoading || isExecutingWorkflow}
                className={`
                  group flex items-center justify-center gap-3
                  h-11 w-11
                  rounded-xl
                  border border-lightGray
                  bg-muted-foreground backdrop-blur-xl
                  shadow-md
                  transition-all duration-300 ease-in-out
                  hover:w-[230px] hover:justify-start hover:px-4
                  hover:bg-muted-foreground
                  active:scale-[0.98]
                  overflow-hidden
                  ${
                    saveWorkflowMutation.isLoading || isExecutingWorkflow
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }
                `}
              >
                {/* Icon */}
                <Search
                  size={16}
                  className="text-background transition-transform group-hover:scale-110 flex-shrink-0"
                />

                {/* Label - hidden by default, shows on hover */}
                <span className="text-background hidden group-hover:inline-block text-sm font-medium tracking-wide whitespace-nowrap animate-in fade-in duration-300">
                  Search Nodes
                </span>

                {/* Shortcut - hidden by default, shows on hover */}
                <div className="hidden group-hover:flex items-center gap-1 px-2 py-1 rounded-lg bg-muted/60 border border-border text-[11px] text-background font-medium whitespace-nowrap animate-in fade-in duration-300">
                  <kbd>Ctrl</kbd>
                  <span>+</span>
                  <kbd>Space</kbd>
                </div>
              </button>

              {/* Export MeshFlow Button - Only show if current flow is marked for export */}
              {activeMeshFlow?.is_export && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleExportFlow();
                  }}
                  disabled={
                    saveWorkflowMutation.isLoading || isExecutingWorkflow
                  }
                  className={`
                    group flex items-center justify-center gap-3
                    h-11 w-11
                    rounded-xl
                    border border-lightGray
                    bg-background/80 backdrop-blur-xl
                    shadow-md
                    transition-all duration-300 ease-in-out
                    hover:w-[160px] hover:justify-start hover:px-4
                    hover:bg-background
                    hover:border-primaryBlue
                    active:scale-[0.98]
                    overflow-hidden
                    ${
                      saveWorkflowMutation.isLoading || isExecutingWorkflow
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }
                  `}
                >
                  {/* Icon */}
                  <Upload
                    size={16}
                    className="text-primaryBlue transition-transform group-hover:scale-110 flex-shrink-0"
                  />

                  {/* Label - hidden by default, shows on hover */}
                  <span className="hidden group-hover:inline-block text-sm font-medium tracking-wide whitespace-nowrap animate-in fade-in duration-300">
                    Export MeshFlow
                  </span>
                </button>
              )}

              {/* Save Meshflow Button - Icon only, expands on hover */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSaveMeshflow();
                }}
                disabled={saveWorkflowMutation.isLoading || isExecutingWorkflow}
                className={`
                  group flex items-center justify-center gap-3
                  h-11 w-11
                  rounded-xl
                  border border-lightGray
                  bg-primaryGreen backdrop-blur-xl
                  shadow-md
                  transition-all duration-300 ease-in-out
                  hover:w-[210px] hover:justify-start hover:px-4
                  hover:bg-primaryGreen
                  active:scale-[0.98]
                  overflow-hidden
                  ${
                    saveWorkflowMutation.isLoading || isExecutingWorkflow
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }
                `}
              >
                {saveWorkflowMutation.isLoading ? (
                  <Loader
                    size={16}
                    className="text-background animate-spin flex-shrink-0"
                  />
                ) : (
                  <Bookmark
                    size={16}
                    className="text-background transition-transform group-hover:scale-110 flex-shrink-0"
                  />
                )}
                <span className="hidden text-background group-hover:inline-block text-sm font-medium tracking-wide whitespace-nowrap animate-in fade-in duration-300">
                  {saveWorkflowMutation.isLoading
                    ? "Saving Meshflow"
                    : "Save Meshflow"}
                </span>

                {/* Shortcut - hidden by default, shows on hover */}
                <div className="hidden group-hover:flex items-center gap-1 px-2 py-1 rounded-lg bg-muted/60 border border-border text-[11px] text-background font-medium whitespace-nowrap animate-in fade-in duration-300">
                  <kbd>Ctrl</kbd>
                  <span>+</span>
                  <kbd>S</kbd>
                </div>
              </button>
            </div>
          )}

          {/* Create Mesh Flow Dialog */}
          <CreateMeshFlowDialog
            showDialog={showCreateMeshFlowDialog}
            setShowDialog={setShowCreateMeshFlowDialog}
            existingMeshFlows={meshFlows}
            onConfirm={
              editingMeshFlow ? handleUpdateMeshFlow : handleCreateMeshFlow
            }
            editingMeshFlow={editingMeshFlow}
            setEditingMeshFlow={setEditingMeshFlow}
            onDelete={handleDeleteMeshFlow}
          />

          {/* Node Search Dialog (Ctrl+N) */}
          <NodeSearchDialog
            open={showNodeSearchDialog}
            onOpenChange={setShowNodeSearchDialog}
            onNodeSelect={handleNodeSearchSelect}
            activeMeshFlowType={activeMeshFlow?.type_workflow}
          />
        </div>

        {edgeMenu && (
          <div
            style={{
              position: "fixed",
              top: edgeMenu.y - 5,
              left: edgeMenu.x + 5,
              zIndex: 1000,
            }}
            className=" border shadow-md rounded-full bg-primary p-2"
            onClick={() => {
              setEdges((eds) => eds.filter((e) => e.id !== edgeMenu.edgeId));
              setEdgeMenu(null);
            }}
          >
            <button className="flex items-center gap-2 text-sm text-background">
              <X size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePluginNode;
